/// <reference path="here-maps.d.ts" />
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { decode } from '@here/flexpolyline';
import PlacesList from './PlacesList.tsx';
import EventsList from './EventsList.tsx';
import PlacesMasonryGallery from './PlacesMasonryGallery.tsx';
import PlaceDetailSheet from './PlaceDetailSheet.tsx';
import Footer from './Footer.tsx';
import { logInfo, logError } from './Logger.js';
import { calculateGoogleRouteMidpoint, calculateDistance, getLatLng, decodePolyline, computeDistanceBetween, interpolate, calculateHaversineDistance } from './MidpointUtils.tsx';

interface Place {
	id: string; // HERE Maps uses 'id' instead of 'place_id'
	place_id: string;
	map_id: string;
	title: string; // HERE Maps uses 'title' instead of 'name'
	address: string; // HERE Maps uses 'address' instead of 'vicinity'
	city: string; // city field (also used for town name or vicinity unique name useful for address simplification for search
	countryName: string;
	state: string;
	position: LatLng; // HERE Maps uses 'position' instead of 'geometry.location'
	averageRating?: number; // HERE Maps uses 'averageRating' instead of 'rating'
	ratingCount?: number; // HERE Maps uses 'ratingCount' instead of 'user_ratings_total'
	openingHours?: { isOpen: boolean, structured: { start: string, end: string }[] }; // Adjusted for HERE Maps
	reviews?: { text: string }[];
	rating?: number;
	user_ratings_total?: number;
	opening_hours?: { open_now: boolean, periods: { close: { time: string } }[] };
	show_photos: boolean;
	limit_detail: boolean;
	photos?: { photo_reference: string }[]; // Relevant for Google places only. HERE Maps does not provide photos
	is_hub?: boolean;
	cse_query?: string;
	cse_acc_query?: string;
	images?: {
		image_url: string;
		caption: string;
		alt_text: string;
		display_priority?: number;
		original_source_url?: string;
		source_platform_url?: string;
		is_hub_hero?: boolean;
		attribution?: string;
	}[];
	isEvent?: boolean;
	eventPhotoUrl?: string;
	eventType?: string;
	genre?: string;
	subGenre?: string;
	priceRange?: string;
	eventDates?: string[];
	ticketUrl?: string;
	description: string;
	strapLine: string;
	reason?: string;
	tags?: string[];
	categories?: { id: string; name: string }[];
	placeType?: string;
	icon?: string;
	fillColor?: string;
	strokeColor?: string;
	textColor?: string;
}

interface LatLng {
	lat: number;
	lng: number;
}

interface RouteSection {
	polyline: { lat: number, lng: number }[];
}

interface Route {
	sections: RouteSection[];
}

interface RoutesResponse {
	routes: Route[];
}

interface Photo {
	link: string;
	contextLink: string;
	title: string;
	snippet: string;
	width: number;
	height: number;
	orientation: string;
	is_hub_hero?: boolean;
	fillColor?: string;
	strokeColor?: string;
	textColor?: string;
	attribution?: string;
	article_body?: string;
}

interface HubWithChildren {
	hub: Place;
	child_places: Place[];
}

export type GalleryItem =
	| { type: 'PHOTO'; id: string; data: Photo }
	| { type: 'AI_PLACE'; id: string; data: Place }
	| { type: 'CURATED_PICK'; id: string; data: Place }
	| { type: 'AI_SUMMARY'; id: string; data: { summary: string; midpoint_town: string; inferred_road: string } }
	| { type: 'EVENT'; id: string; data: any }
	| { type: 'SECTION_DIVIDER'; id: string; label: string };


const MapBetweenPlaces: React.FC = () => {
	const mapInstance = useRef<H.Map | null>(null);
	const [location1, setLocation1] = useState('');
	const [location2, setLocation2] = useState('');
	const [location1LL, setLocation1LL] = useState<LatLng | null>(null);
	const [location2LL, setLocation2LL] = useState<LatLng | null>(null);
	const [midpoint, setMidpoint] = useState<LatLng | null>(null);
	const [mainRouteIndex, setMainRouteIndex] = useState(0);
	const [totalMainRouteDistance, setTotalMainRouteDistance] = useState('');
	const [changeSource, setChangeSource] = useState('input');
	const [mapBoundingBox, setMapBoundingBox] = useState(null);
	const [highwayPlaces, setHighwayPlaces] = useState([]);
	const [hubPlaces, setHubPlaces] = useState([]);
	const [curatedPicks, setCuratedPicks] = useState<Place[]>([]);
	const [places, setPlaces] = useState<Place[]>([]);
	const [eventVenues, setEventVenues] = useState<Place[]>([]);
	const [events, setEvents] = useState<Place[]>([]);
	const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
	const [placesWithMapIds, setPlacesWithMapIds] = useState<Place[]>([]);
	const [likedPlaces, setLikedPlaces] = useState<string[]>([]);
	const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
	const [markers, setMarkers] = useState<H.map.Marker[]>([]);
	const [routes, setRoutes] = useState<any[]>([]);
	const [error, setError] = useState('');
	const mapRef = useRef<HTMLDivElement>(null);
	const inputRef1 = useRef<HTMLInputElement>(null);
	const inputRef2 = useRef<HTMLInputElement>(null);
	const routeRenderers = useRef<H.map.Polyline[]>([]);
	const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
	const initialSelectedTypes: string[] = ['Eat & Drink'];
	const [radius, setRadius] = useState<number>(15);
	const [radiusInput, setRadiusInput] = useState<string>('10');
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [focalPoint, setFocalPoint] = useState<LatLng | null>(null);
	const midpointMarkers = useRef<H.map.Marker[]>([]);
	const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
	const selectedPlaceRef = useRef<Place | null>(null);
	const [placeDetailSheet, setPlaceDetailSheet] = useState<Place | null>(null);
	const [selectedEvent, setSelectedEvent] = useState<Place | null>(null);
	const selectedEventRef = useRef<Place | null>(null);
	const [initLoadSelectedPlaceId, setInitLoadSelectedPlaceId] = useState<string | null>(null);
	const sheetOpenedViaDeepLinkRef = useRef(false);
	const placeDetailSheetRef = useRef<HTMLDivElement>(null);
	const placeRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
	const eventRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
	const [expandedReviews, setExpandedReviews] = useState<{ [key: string]: boolean }>({});
	const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: string]: boolean }>({});
	const apiKey = 'MASK';
	const ticketMasterApiKey = 'MASK';
	const hereApiKey = 'MASK';
	const customSearchEngineID = 'MASK';
	const CENTRAL_UK_POSITION = '54.0,-2.0,500000'; // Latitude, Longitude, Radius in meters
	const TOLERANCE = 0.00001;
	const markersRef = useRef([]);
	const eventMarkersRef = useRef([]);
	const hubMarkersRef = useRef([]);
	const [isMapView, setIsMapView] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const initialLoadFromUrlParamsRef = useRef(false);
	const [initialContentDisplayed, setInitialContentDisplayed] = useState(true);
	const [featuredHubPhotos, setFeaturedHubPhotos] = useState<Photo[]>([]);
	const [aiPrompt, setAiPrompt] = useState('');
	const [searchType, setSearchType] = useState('ai'); // Default to AI
	const [isFindButtonDropdownOpen, setIsFindButtonDropdownOpen] = useState(false);
	const [isMapPlacesListVisible, setIsMapPlacesListVisible] = useState(false);
	const [hideEvents] = useState(() => new URLSearchParams(window.location.search).get('hideEvents') === '1');
	const [hideHerePlaces] = useState(() => new URLSearchParams(window.location.search).get('hideHerePlaces') === '1');
	const [hideHubs] = useState(() => new URLSearchParams(window.location.search).get('hideHubs') === '1');
	const hasLoadedPlacesData = useRef(false);
	const queryPrefixes = {
		'Eat & Drink': 'cafe or restaurant',
		'Going Out': 'best pub OR bar interior',
		'Sights & Museums': 'historical sites museums landmarks',
		'Parks & Outdoor': 'walking or parks or gardens or lakes',
		'Accommodation': 'hotels',
		'Shopping': 'shopping centres shops and markets',
		'Business & Services': 'tourist places to see',
		'Transport': 'tourist places to see',
		'Facilities': 'tourist places to see',
		'Health': 'tourist places to see',
		'Petrol & Charge Stations': 'best cafe or coffee or restaurant -cup -mug'
	};
	const undesiredImageSources = ['x-raw-image:///', 'ebayimg.com', 'govmint.com', 'muscache.com', 'tiktok.com', 'facebook', 'm.facebook.com', 'ytko.com', 'hygiene', 'fbsbx.com'];
	const undesiredImageTitles = ['Rentals', 'Rent', 'Renting', 'Holiday Cottage', 'Burger King', 'BBK', 'Fast Food', 'Amazon', 'Stanhope', 'hygiene', 'Touring Park'];
	const hereCategoryDiscoverTermsMapping: Record<string, string[]> = {
		'Eat & Drink': ['coffee', 'cafe', 'restaurant', 'pub', 'bar'],
		'Accommodation': ['hotel', 'motel'],
		'Petrol & Charge Stations': ['EV Charging', 'petrol station', 'charging station']
	};
	const hereTypeMapping: Record<string, string> = {
		'Eat & Drink': 'Eat & Drink',
		'Accommodation': 'Accommodation',
		'Petrol & Charge Stations': 'EV Charging',
		'Going Out': 'Going Out'
	};
	// Category ID mapping based on HERE Places taxonomy
	const hereCategoryMapping: Record<string, string[]> = {
		'Eat & Drink': ['100'], // top-level Eat & Drink category
		'Accommodation': ['200'], // top-level Accommodation category
		'Petrol & Charge Stations': [
			'700-7600-0116', // Petrol / Gas station
			'700-7600-0323'  // EV charging station
		]
	};

	const markerIcons: Record<string, string> = {
		'Eat & Drink': '/green-dot.png',
		'EV Charging': '/chargingicon.png',
		'Petrol Station': '/logoblue-dot.png',
		'Event': '/logoblue-dot.png',
		'Highway Services': '/logoblue-dot.png'
	};

	// Category definitions
	const hereCategoryTypesAndQueries: Record<string, { queries: string[], types: string[] }> = {
		'Eat & Drink': {
			//queries: ['Eat & Drink', 'EV Charging'],
			queries: ['Eat & Drink'],
			types: ['Eat & Drink', 'EV Charging']
		},
		'Petrol Station': {
			queries: ['Petrol Station'],
			types: ['Petrol Station']
		}
	};

	const countryNameToCode: Record<string, string> = {
		'United Kingdom': 'UK',
		'England': 'UK', // fallback for state-based naming
		'Scotland': 'UK',
		'Wales': 'UK',
		'Northern Ireland': 'UK',
		'United States': 'US',
		'Canada': 'CA',
		'Australia': 'AU',
		'Germany': 'DE',
		'France': 'FR',
		'Italy': 'IT',
		'Spain': 'ES',
		'Japan': 'JP',
		'South Korea': 'KR',
		'India': 'IN',
		'Brazil': 'BR',
		'Mexico': 'MX',
		// Add more as needed
	};

	const DEFAULT_HUB_COLORS = {
		fillColor: "#00e64d",
		strokeColor: "#0a7a35",
		textColor: "#000000"
	};

	// Highway places filtering toggle
	const FILTER_HIGHWAY_PLACES_NEAR_MIDPOINT = true; // ← toggle this to false to show all
	// Radius around midpoint to include highway places (in miles)
	const HIGHWAY_MIDPOINT_RADIUS_MILES = 20;
	// Show Halfway Label
	const SHOW_MIDPOINT_LABEL = true;
	// Allow toggling of markers to enable presentation simplification or detail
	const showHubMarkersRef = useRef(true);
	const showPlaceMarkersRef = useRef(false);

	const getCity = (address) => address?.split(',')[0].trim();
	const city1 = getCity(location1);
	const city2 = getCity(location2);

	const distanceSuffix = totalMainRouteDistance
		? ` ${Math.round(Number(totalMainRouteDistance))}km`
		: '';

	const dynamicTitle = (city1 && city2)
		? `Trip from ${city1} to ${city2}${distanceSuffix}`
		: (city1 ? `From ${city1}` : '');

	const SEARCH_SUGGESTIONS = [
		"Ask for anything — e.g. great coffee with a view...",
		"Cosy pub with a fireplace",
		"Somewhere dog-friendly with a walk nearby",
		"A boutique stay with a view",
		"Lunch with outside play for kids",
		"Surprise me"
	];

	const NOAI_SEARCH_DEFAULT = 'Great Coffee, Food, Drink, Pretty Places, Boutique Stays';

	const [suggestionIndex, setSuggestionIndex] = useState(0);


	const initializeMap = () => {
		if (mapRef && mapRef.current && !mapInstance.current) {
			console.log('initializeMap called and mapRef exists with mapInstance needing creating');
			try {
				const platform = new H.service.Platform({
					apikey: 'gNvWwWqH2ZqRIBOn5baPWgQWODLvB9ZBV_fLfQajXeY',
				});
				const defaultLayers = platform.createDefaultLayers();
				const map = new H.Map(
					mapRef.current,
					defaultLayers.vector.normal.map,
					{
						zoom: 13,
						center: { lat: 51.5074, lng: -0.1278 }, // Set a valid initial center (e.g., London)
					}
				);
				mapInstance.current = map;
				(window as any).__hereMap = map;

				// Add map events
				const mapEvents = new H.mapevents.MapEvents(map);
				const behavior = new H.mapevents.Behavior(mapEvents); // Default settings enable pinch zoom
				const ui = H.ui.UI.createDefault(map, defaultLayers);

				/*
				// Set pointer-events to auto by default
				map.getElement().style.pointerEvents = 'auto';
		  
				let pressTimer;
		  
				const startPressTimer = (evt) => {
				  pressTimer = window.setTimeout(() => {
					const coord = map.screenToGeo(evt.currentPointer ? evt.currentPointer.viewportX : evt.touches[0].clientX, evt.currentPointer ? evt.currentPointer.viewportY : evt.touches[0].clientY);
					if (coord) {
					  const newFocalPoint = { lat: coord.lat, lng: coord.lng };
					  setFocalPoint(newFocalPoint);
					}
				  }, 2500); // x second long press
				};
		  
				const clearPressTimer = () => {
				  clearTimeout(pressTimer);
				};
		  
			    
				if (isMobileOrTouchTablet()) {
				  // Add touch events for mobile devices
				  map.addEventListener('touchstart', startPressTimer);
				  map.addEventListener('touchend', clearPressTimer);
				  map.addEventListener('touchcancel', clearPressTimer);
				} else {
				  // Add pointer events for desktop devices
				  map.addEventListener('pointerdown', startPressTimer);
				  map.addEventListener('pointerup', clearPressTimer);
				  map.addEventListener('pointerleave', clearPressTimer);
				}
				*/

			} catch (error) {
				console.error('Error initializing the map:', error);
			}
		} else if (!mapRef) {
			console.log('initializeMap called but mapRef null ');
		} else if (!mapInstance.current) {
			console.log('initializeMap called and mapInstance.current null ');
		}
	};

	const toggleMapView = () => {
		setIsMapView((prevState) => !prevState);
	};

	useEffect(() => {
		if (mapInstance.current) {
			const mapContainer = mapInstance.current.getElement();
			const mapWrapper = mapContainer.closest('.map-wrapper') || mapContainer.closest('.map-wrapper-map-view');
			const mapOverlay = mapWrapper.querySelector('.map-overlay');

			let timer;

			if (isMapView) {

				// 1. Hide the list immediately so the map can resize cleanly
				setIsMapPlacesListVisible(false);

				if (!showPlaceMarkersRef.current) {
					// MAP IMAGE GRAB HELPER: Switch to false for Map screen grabbing of hubs
					showPlaceMarkersRef.current = true;
					updateDisplayedMarkers();
				}

				let heightStyle = '';
				if (isMobileOrTouchTablet()) {
					heightStyle = `${window.innerHeight}px`; // Or adjust as needed.
				} else {
					heightStyle = '100vh'; // Default
				}
				mapWrapper.style.height = heightStyle;

				if (mapOverlay) {
					mapOverlay.classList.add('hidden'); // Hide the overlay
				}

				// 2. The Wait: Reveal the list only after the map resize/paint is likely done
				timer = setTimeout(() => setIsMapPlacesListVisible(true), 250);

			} else { // If going back to list-view
				console.log('isMapView useEffect hook - not mapview so setting list view height');

				// 3. Ensure it's off when not in map view
				setIsMapPlacesListVisible(false);

				// MAP IMAGE GRAB HELPER: Uncomment and switch to false for Map screen grabbing of hubs
				//showPlaceMarkersRef.current = false;
				//updateDisplayedMarkers();

				setMapWrapperHeight();
				if (mapOverlay) {
					mapOverlay.classList.remove('hidden'); // Show the overlay
				}
			}

			console.log(
				"Map container size:",
				mapContainer.offsetWidth,
				mapContainer.offsetHeight
			);

			mapInstance.current.getViewPort().resize();
			window.dispatchEvent(new Event("resize"));

			return () => clearTimeout(timer);
		}
	}, [isMapView]);

	// Nothing yet has forced the mac scrollbars to display initially. 
	// Some Mac Users not aware places list is scrollable and need hint.

	useEffect(() => {
		if (isMapView && isMapPlacesListVisible) {
			const timer = setTimeout(() => {
				const el = document.querySelector('.places-overlay');
				if (el) {
					el.scrollLeft = 1; // Nudge it 1px to the right
					el.scrollLeft = 0; // Snap it back
				}
			}, 300); // Wait until after the mount delay
			return () => clearTimeout(timer);
		}
	}, [isMapView, isMapPlacesListVisible]);

	const handleListViewClick = () => {
		if (isMapView) {
			toggleMapView();
			waitForInputAndSetupAutocomplete();
		}
	}

	const handleMapViewClick = () => {
		if (!isMapView) {
			toggleMapView();
		}
	}


	const waitForInputAndSetupAutocomplete = () => {
		const interval = setInterval(() => {
			if (inputRef1 && inputRef2) {
				setupAutocomplete(inputRef1, setLocation1);
				setupAutocomplete(inputRef2, setLocation2);
				clearInterval(interval);
			}
		}, 100);
	};

	const setMapWrapperHeight = () => {
		const mapWrapper = document.querySelector('.map-wrapper') || document.querySelector('.map-wrapper-map-view');
		const mapWrapperListView = document.querySelector('.map-wrapper-listview-height');

		if (mapWrapperListView) {
			const computedStyle = window.getComputedStyle(mapWrapperListView);
			const defaultHeight = computedStyle.getPropertyValue('height');
			mapWrapper.style.height = defaultHeight; // Directly set the height
		} else {
			console.error('Element .map-wrapper-listview-height not found');
		}
	};

	// Add event listener for window resize
	window.addEventListener('resize', () => {
		if (mapInstance.current) {
			mapInstance.current.getViewPort().resize();
		}
	});

	const isIOS = () => {
		return /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);
	};

	const isiPhone = () => {
		return /iPhone/i.test(navigator.userAgent);
	};

	const isAndroid = () => {
		return /Android/i.test(navigator.userAgent);
	};

	const isMobileOrTouchTablet = () => {
		return /iPhone|iPod|Android/i.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);
	};

	const isMobile = () => {
		return isiPhone() || isAndroid();
	};


	const mapRefCallback = (node) => {
		if (node) {
			mapRef.current = node;
			initializeMap();
		}
	};



	// Debounce function
	const debounce = (func: Function, delay: number) => {
		let timeoutId: NodeJS.Timeout;
		return (...args: any[]) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => func(...args), delay);
		};
	};

	const getHereSuggestions = async (query: string) => {
		if (query && query.length > 2) {
			try {
				console.log('making autosuggest API call for ' + query);
				const response = await axios.get(`/heresuggest/v1/autosuggest`, {
					params: {
						q: query,
						at: CENTRAL_UK_POSITION,
						apiKey: hereApiKey
					}
				});
				return response.data.items;
			} catch (error) {
				console.error('Error fetching suggestions:', error);
				if (error instanceof Error) {
					console.error('Error message:', error.message);
				} else {
					console.error('Unexpected error:', error);
				}
				throw error;
			}
		}
	};

	const setupAutocomplete = (
		inputRef: React.RefObject<HTMLInputElement | null>,
		setLocation: (location: string) => void
	) => {
		if (inputRef.current && inputRef.current.parentNode) {
			const suggestionsContainer = document.createElement('div');
			suggestionsContainer.className = 'suggestions-dropdown';
			inputRef.current.parentNode.appendChild(suggestionsContainer);

			let hasSelectedSuggestion = false;
			let lastSelectedValue = '';

			const handleInput = debounce(async () => {
				if (inputRef.current) {
					const query = inputRef.current.value;

					// Reset selection flag if user starts typing something new
					if (query !== lastSelectedValue) {
						hasSelectedSuggestion = false;
						lastSelectedValue = '';
					}

					if (query.length > 2) {
						const suggestions = await getHereSuggestions(query);
						suggestionsContainer.innerHTML = '';

						if (suggestions && suggestions.length > 0) {
							suggestionsContainer.style.display = 'block';

							suggestions.forEach((suggestion: any) => {
								if (suggestion.address?.label) {
									const suggestionDiv = document.createElement('div');
									suggestionDiv.textContent = suggestion.address.label;

									suggestionDiv.addEventListener('click', () => {
										if (inputRef.current) {
											inputRef.current.value = suggestion.address.label;
											lastSelectedValue = suggestion.address.label;
											hasSelectedSuggestion = true;

											suggestionsContainer.innerHTML = '';
											suggestionsContainer.style.display = 'none';
											setLocation(suggestion.address.label || '');
											setChangeSource('input');
										}
									});

									suggestionsContainer.appendChild(suggestionDiv);
								}
							});
						} else {
							suggestionsContainer.style.display = 'none';
						}
					} else {
						suggestionsContainer.innerHTML = '';
						suggestionsContainer.style.display = 'none';
					}
				}
			}, 425);

			inputRef.current.addEventListener('input', handleInput);

			// Detect when user returns to the field
			inputRef.current.addEventListener('focus', () => {
				if (
					hasSelectedSuggestion &&
					inputRef.current?.value === lastSelectedValue
				) {
					inputRef.current.value = '';
					hasSelectedSuggestion = false;
					lastSelectedValue = '';
				}
			});

			// Hide dropdown when clicking outside
			document.addEventListener('click', (event) => {
				if (
					!suggestionsContainer.contains(event.target as Node) &&
					event.target !== inputRef.current
				) {
					suggestionsContainer.innerHTML = '';
					suggestionsContainer.style.display = 'none';
				}
			});
		}
	};

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!hasLoadedPlacesData.current) {
			logInfo('Default hook run with first data load');
			hasLoadedPlacesData.current = true;
			loadHighwayPlaces();
			loadHubPlaces();

			setSelectedTypes(initialSelectedTypes);
			const { urlLocation1, urlLocation2, routeIndex, searchType, aiPrompt, urlFilters, urlLikedPlaces, urlSelectedPlaceId, urlMode } = getUrlParams();

			setTimeout(() => {

				if (urlLocation1) {
					setLocation1(urlLocation1);
					setInitialContentDisplayed(false);
					initialLoadFromUrlParamsRef.current = true;
				}
				if (urlLocation2) {
					setLocation2(urlLocation2);
					initialLoadFromUrlParamsRef.current = true;
				}

				if (searchType && searchType.length > 0) {
					setSearchType(searchType);
					initialLoadFromUrlParamsRef.current = true;
				}
				if (aiPrompt && aiPrompt.length > 0) {
					setAiPrompt(aiPrompt);
					initialLoadFromUrlParamsRef.current = true;
				}

				if (routeIndex && !isNaN(Number(routeIndex))) {
					setMainRouteIndex(Number(routeIndex)); // Convert routeIndex to a number if valid
				} else {
					setMainRouteIndex(0); // Default to 0 if routeIndex is empty or not a number
				}

				if (urlSelectedPlaceId) {
					setInitLoadSelectedPlaceId(urlSelectedPlaceId);
				}
				if (urlFilters && urlFilters.length > 0) setSelectedTypes(urlFilters);
				if (urlLikedPlaces && urlLikedPlaces.length > 0) setLikedPlaces(urlLikedPlaces);

				setupAutocomplete(inputRef1, setLocation1);
				setupAutocomplete(inputRef2, setLocation2);

				if (urlMode === 'ai') {
					setMode('ai');
				}


			}, 500); // Adjust the delay time as needed (500ms in this example)
		}
	}, []);

	// Nudge the map so the selected marker is visible above the detail sheet.
	// Called after the initial setCenter has settled; fails silently if anything is unready.
	const applySheetMarkerOffset = (position: { lat: number; lng: number }) => {
		if (!mapInstance.current || !placeDetailSheetRef.current) return;
		const panelHeight = placeDetailSheetRef.current.getBoundingClientRect().height;
		if (panelHeight <= 0) return;
		// After setCenter(marker), the marker sits at the viewport centre.
		// screenToGeo at (centreX, centreY + panelHeight/2) gives the geo point
		// half a panel-height below the marker. Setting that as the new centre
		// shifts the map downward, pulling the marker up into the visible strip above the panel.
		const cx = window.innerWidth / 2;
		const cy = window.innerHeight / 2;
		const offsetGeo = mapInstance.current.screenToGeo(cx, cy + panelHeight / 2);
		if (offsetGeo) {
			mapInstance.current.setCenter({ lat: (offsetGeo as any).lat, lng: (offsetGeo as any).lng });
		}
	};

	// When a selectedPlaceId arrives from URL and curated picks are loaded, open the detail sheet immediately
	useEffect(() => {
		if (!initLoadSelectedPlaceId || curatedPicks.length === 0) return;
		const pick = curatedPicks.find(p => p.place_id === initLoadSelectedPlaceId);
		if (!pick) return;

		setSelectedPlace(pick);
		setPlaceDetailSheet(pick);
		sheetOpenedViaDeepLinkRef.current = true;

		// Wait for the map to be fully initialised before switching to map view,
		// so the isMapView useEffect can set mapWrapper.style.height = '100vh'.
		const activate = () => {
			setIsMapView(true);
			// Centre after the map wrapper has resized (isMapView useEffect fires first)
			setTimeout(() => {
				if (mapInstance.current && pick.position) {
					mapInstance.current.setCenter({ lat: pick.position.lat, lng: pick.position.lng });
					mapInstance.current.setZoom(13);
					mapInstance.current.getViewPort().resize();
				}
			}, 400);
			// Additive: after the initial pan settles, nudge the map so the marker
			// is visible in the strip above the detail sheet.
			setTimeout(() => {
				if (sheetOpenedViaDeepLinkRef.current && pick.position) {
					applySheetMarkerOffset(pick.position);
				}
			}, 550);
		};

		if (mapInstance.current) {
			activate();
		} else {
			// Map not initialised yet — poll until it is (give up after 6s)
			const poll = setInterval(() => {
				if (mapInstance.current) {
					clearInterval(poll);
					activate();
				}
			}, 100);
			setTimeout(() => clearInterval(poll), 6000);
		}
	}, [initLoadSelectedPlaceId, curatedPicks]);

	const filterPlacesWithinBounds = (places, bounds) => {
		return places.filter(place => {
			const { lat, lng } = place.position;
			return lat >= bounds.minLat && lat <= bounds.maxLat && lng >= bounds.minLng && lng <= bounds.maxLng;
		});
	};


	const loadHighwayPlacesKLM = async () => {
		const response = await fetch('/roadsidecafes.kml');
		const kmlText = await response.text();
		const highwayPlaces = parseKML(kmlText);
		const places = highwayPlaces.map((place, index) => ({
			...place,
			id: `highwayPlace-${index}`, // Generate a unique ID
			place_id: `highwayPlace-${index}`,
			limit_detail: true,
			address: place.name,
			categories: [{ name: 'Coffee Shop' }], // Set category correctly
			title: 'Costa Coffee',
			position: {
				lat: place.latitude,
				lng: place.longitude
			}
		}));
		setHighwayPlaces(places);
	};

	const loadHighwayPlaces = async () => {
		const response = await fetch('/roadsideplaces.json');
		const jsonData = await response.json();
		const highwayPlaces = parseJSON(jsonData);
		const places = highwayPlaces.map((place, index) => ({
			...place,
			id: `highwayPlace-${index}`, // Generate a unique ID
			place_id: `highwayPlace-${index}`,
			limit_detail: true,
			address: place.name,
			categories: [{ name: 'Road Services' }],
			title: place.places.join(', '), // Dynamically set title based on places array
			position: {
				lat: place.latitude,
				lng: place.longitude
			},
			placeType: "Highway Services",
			icon: markerIcons["Highway Services"]
		}));
		setHighwayPlaces(places);
	};

	const loadHubPlaces = async () => {
		try {
			const response = await fetch('/wib-hubplaces.json');
			const jsonData = await response.json();

			// Separate curated picks (our_pick) from true hub settlements
			const hubEntries = jsonData.filter((entry: any) => entry.place_type !== 'our_pick');
			const pickEntries = jsonData.filter((entry: any) => entry.place_type === 'our_pick');

			const picks: Place[] = pickEntries.map((pick: any, index: number) => ({
				id: `CuratedPick:${index}`,
				place_id: pick.place_id,
				title: pick.title,
				address: pick.city || pick.title,
				city: pick.city || pick.title,
				countryName: 'UK',
				state: 'England',
				position: pick.position,
				description: pick.description || '',
				strapLine: pick.short_description ? pick.short_description.slice(0, 70).trimEnd() + (pick.short_description.length > 70 ? '…' : '') : '',
				reason: pick.reason || '',
				tags: pick.tags || [],
				categories: [{ id: 'curated', name: pick.category_text || 'Our Pick' }],
				placeType: 'CURATED_PICK',
				show_photos: true,
				limit_detail: true,
				is_hub: false,
				images: pick.images || [],
			}));
			setCuratedPicks(picks);

			const structuredHubs: HubWithChildren[] = hubEntries.map((hub: any, index: number) => {
				const hubPlace: Place = {
					id: `Hub:${index}`,
					place_id: hub.place_id,
					title: hub.title,
					address: hub.title,
					city: hub.title,
					countryName: 'UK',
					state: 'England',
					position: hub.position,
					description: hub.description,
					strapLine: hub.strap_line,
					categories: [{
						id: 'hub',
						name: hub.category_text || 'Our Pick'
					}],
					show_photos: true,
					limit_detail: true,
					is_hub: hub.is_hub ?? false,
					images: hub.images || [],
					fillColor: hub.fillColor ?? DEFAULT_HUB_COLORS.fillColor,
					strokeColor: hub.strokeColor ?? DEFAULT_HUB_COLORS.strokeColor,
					textColor: hub.textColor ?? DEFAULT_HUB_COLORS.textColor,
					...(hub.is_hub !== undefined && { is_hub: hub.is_hub }),
					...(hub.cse_query && { cse_query: hub.cse_query }),
					...(hub.cse_acc_query && { cse_acc_query: hub.cse_acc_query })
				};

				const childPlaces: Place[] = (hub.child_places || []).map((child, childIndex) => ({
					id: `hub:${index}:place:${childIndex}`,
					place_id: child.place_id,
					title: child.title,
					address: child.title,
					city: hub.title,
					countryName: 'UK',
					state: 'England',
					position: child.position,
					description: child.description,
					strapLine: hub.strap_line,
					categories: [{ id: 'child', name: 'Our Pick' }],
					our_pick: true,
					show_photos: true,
					limit_detail: true,
					images: child.images || [],
					fillColor: child.fillColor ?? hub.fillColor ?? DEFAULT_HUB_COLORS.fillColor,
					strokeColor: child.strokeColor ?? hub.strokeColor ?? DEFAULT_HUB_COLORS.strokeColor,
					textColor: child.textColor ?? hub.textColor ?? DEFAULT_HUB_COLORS.textColor
				}));

				return { hub: hubPlace, child_places: childPlaces };
			});

			setHubPlaces(structuredHubs); // now holds structured data
		} catch (error) {
			console.error('Failed to load hub places:', error);
		}
	};


	const parseJSON = (jsonData) => {
		const locations = jsonData.map((item) => ({
			name: item["Service Station"],
			latitude: parseFloat(item.Coordinates.split(',')[1]),
			longitude: parseFloat(item.Coordinates.split(',')[0]),
			places: item.Places
		}));
		return locations;
	};



	const parseKML = (kmlText) => {
		const parser = new DOMParser();
		const kmlDoc = parser.parseFromString(kmlText, 'application/xml');
		const placemarks = kmlDoc.getElementsByTagName('Placemark');
		const locations = [];

		for (let i = 0; i < placemarks.length; i++) {
			const placemark = placemarks[i];
			const name = placemark.getElementsByTagName('name')[0].textContent;
			const coordinates = placemark.getElementsByTagName('coordinates')[0].textContent.trim().split(',');

			locations.push({
				name,
				latitude: parseFloat(coordinates[1]),
				longitude: parseFloat(coordinates[0])
			});
		}

		return locations;
	};


	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		// Only execute if the "Gate" initialLoadFromUrlParamsRef is open AND we have the data
		// The purpose is to handle a shared url load given the state has a slight delay updating location1 etc 
		if (initialLoadFromUrlParamsRef.current && location1 && location2) {

			console.info("Gatekeeper: State is ready. Executing initial search...");

			// Close the gate immediately so this NEVER runs again automatically
			initialLoadFromUrlParamsRef.current = false;

			const runInitialLoadSearch = async () => {
				// Now that state is synced, onExecuteSearch will find the correct location1/2
				await onExecuteSearch();

				// Handle selecting the specific place from the URL if applicable
				checkAndHandleInitLoadSelectedPlace();
			};

			runInitialLoadSearch();
		}
	}, [location1, location2]);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (changeSource === 'route') {
			if (searchType === 'ai') {
				handleAiSubmit();
			} else {
				handleFind();
			}

		}
	}, [mainRouteIndex]);

	useEffect(() => {
		selectedPlaceRef.current = selectedPlace;
	}, [selectedPlace]);

	useEffect(() => {
		if (focalPoint && mapInstance.current) {
			clearPlaces();
			clearMarkers();

			const map = mapInstance.current;

			map.setCenter(focalPoint);
			//map.setZoom(14);

			// Create padding around the focal point
			const paddingDistance = 0.1; // Adjust this value as needed
			const northEast = { lat: focalPoint.lat + paddingDistance, lng: focalPoint.lng + paddingDistance };
			const southWest = { lat: focalPoint.lat - paddingDistance, lng: focalPoint.lng - paddingDistance };

			const bounds = new H.geo.Rect(
				northEast.lat, northEast.lng,
				southWest.lat, southWest.lng
			);
			//map.getViewModel().setLookAtData({ bounds });

			const focalPointMarker = new H.map.Marker(focalPoint, {
				icon: new H.map.Icon('http://maps.google.com/mapfiles/ms/icons/blue-dot.png', { size: { w: 30, h: 30 } }),
				data: 'Focal Point' // Provide an empty string or any relevant data
			});

			// Set the title as data on the marker
			focalPointMarker.setData('Focal Point');


			map.addObject(focalPointMarker);
			midpointMarkers.current.push(focalPointMarker);

			findNearbyPlaces(focalPoint);
		}
	}, [focalPoint]);

	const runFindPlacesForLocation1 = async () => {
		try {
			const newFocalPoint = await getCoordinates(location1);

			if (newFocalPoint) {
				setLocation1LL(newFocalPoint);

				setFocalPoint(newFocalPoint);

				await findNearbyPlaces(newFocalPoint);
			}
		} catch (error) {
			setError((error as Error).message);
		}
	}
	const checkAndHandleInitLoadSelectedPlace = () => {
		if (initLoadSelectedPlaceId && initLoadSelectedPlaceId.length > 0) {
			const place = places.find(p => p.place_id === initLoadSelectedPlaceId);
			if (place) {
				selectedPlaceRef.current = place;
				setSelectedPlace(place);
				setInitLoadSelectedPlaceId(''); // clear it so it wont be proceeded twice
			}
		}
	}

	const centerMapOnMarker = (marker) => {
		if (mapInstance.current && marker) {
			const map = mapInstance.current;
			map.setCenter(marker.getGeometry());
		}
	}

	const focusMapOnMarker = (marker, place) => {
		if (mapInstance.current && marker) {
			const map = mapInstance.current;
			centerMapOnMarker(marker);

			const originalLat = marker.originalLat;
			const originalLng = marker.originalLng;

			let animationSteps = 30; // Increased steps for smoother and slower animation
			const animationDuration = 2000; // Increased duration for slower animation
			const stepDuration = animationDuration / animationSteps;

			if (isIOS()) {
				animationSteps = 60;
			}

			let currentStep = 0;

			const zoomLevel = map.getZoom();
			const baseIncrement = 0.01;
			const baseZoomLevel = 12;
			const zoomRatio = zoomLevel / baseZoomLevel;
			let increment = baseIncrement / Math.pow(zoomRatio, 3);

			if (zoomLevel < baseZoomLevel) {
				increment *= 1.5;
			} else if (zoomLevel > 15) {
				increment *= 0.5;
			}

			const animate = () => {
				currentStep++;
				if (currentStep <= animationSteps) {
					const progress = currentStep / animationSteps;
					const newLat = originalLat + increment * progress;
					marker.setGeometry(new H.geo.Point(newLat, originalLng));
					requestAnimationFrame(animate);
				} else {
					marker.setGeometry(new H.geo.Point(originalLat, originalLng));
				}
			};

			requestAnimationFrame(animate);
		}
	};



	const assignMapIds = (places) => {
		if (places.length > 0) {
			places.forEach((place, index) => {
				if (!place.isEvent) {
					place.map_id = index + 1;
				}
			});
		}
	};

	const assignEventVenueMapIds = (places) => {
		if (places.length > 0) {
			places.forEach((place, index) => {
				place.map_id = 'E' + (index + 1);
			});
		}
	};

	const createMarkers = async () => {
		if (places.length > 0 && mapInstance.current) {
			const map = mapInstance.current;

			const allCoordinates = [];

			const newMarkers = await Promise.all(places.map(async (place) => {

				if (place.is_hub || place.placeType == 'AI_GEM') { return; }

				let icon: H.map.Icon;

				// If it's EV Charging → just use the plain image (no text overlay)
				if (place.placeType === "EV Charging") {
					icon = new H.map.Icon(markerIcons["EV Charging"], {
						size: { w: 15, h: 20 },
					});
				}
				// blue base dot + blue circle + text overlay
				else if (place.placeType === "Petrol Station") {
					icon = createPinIconSvgTaperedWithText(
						"#16B0F9",
						"#0b6fa8",
						place.map_id,
						1.25,
						26,
						32
					);
				}
				else if (place.placeType === "Highway Services") {

					icon = createPinIconSvgTaperedWithText(
						"#16B0F9",
						"#0b6fa8",
						place.map_id,
						1.25,
						26,
						32
					);
				}
				// green base dot + green circle + text overlay
				else {
					icon = createPinIconSvgTaperedWithText(
						"#00e64d",        // fill
						"#0a7a35",        // stroke
						place.map_id,
						1.25,
						26,
						32
					);
				}

				const lat = Number(place.position.lat);
				const lng = Number(place.position.lng);

				const marker = new H.map.Marker(
					{ lat: lat, lng: lng },
					{
						icon: icon,
						data: place.id,
					}
				);

				// Store original latitude and longitude
				marker.originalLat = lat;
				marker.originalLng = lng;

				allCoordinates.push({ lat: lat, lng: lng });

				// Set zIndex based on map_id (lower map_id gets higher zIndex)
				marker.setZIndex(2000 - place.map_id);

				marker.addEventListener('tap', () => {
					if (place && placeRefs.current) {
						const placeRef = placeRefs.current[place.id];
						if (placeRef) {
							placeRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}
						selectedPlaceRef.current = place;
						setSelectedPlace(place);

						const bounds = calculateBoundingBox(allCoordinates);
						const mapContainer = mapRef.current;
						const zoomLevel = calculateZoomLevel(bounds, mapContainer.offsetWidth, mapContainer.offsetHeight);

						const currentZoom = map.getZoom();
						const zoomTolerance = 2; // Define a tolerance value

						console.log('createMarkers: currentZoom=' + currentZoom + ' setting new zoom=' + zoomLevel);
					}
				});

				map.addObject(marker);
				return marker;
			}));

			console.log('createMarkers adding markers ' + newMarkers.length);
			markersRef.current = newMarkers;
		}
	};

	const createHubAndGemMarkers = async () => {
		if (places.length > 0 && mapInstance.current) {
			const map = mapInstance.current;

			const allCoordinates = [];

			const newMarkers = await Promise.all(places.map(async (place) => {

				//if (!place.is_hub || place.placeType != 'AI_GEM') { return; }

				let icon: H.map.Icon;

				if (place.is_hub) {

					icon = createHubPinIconSvgWithText(
						place.map_id,
						place.fillColor,
						place.strokeColor,
						1.25,
						36,
						52,
						{ fontSize: 14, bold: false }
					);
				} else if (place.placeType == 'AI_GEM') {

					icon = createPinIconSvgTaperedWithText(
						place.fillColor || '#38CEF5',
						place.strokeColor || '#01A2DE',
						place.map_id,
						1.25,
						36,
						42,
						{ fontSize: 12, bold: false }
					);

				} else {
					return;
				}
				const lat = Number(place.position.lat);
				const lng = Number(place.position.lng);

				const marker = new H.map.Marker(
					{ lat: lat, lng: lng },
					{
						icon: icon,
						data: place.id,
					}
				);

				// Store original latitude and longitude
				marker.originalLat = lat;
				marker.originalLng = lng;

				allCoordinates.push({ lat: lat, lng: lng });

				// Set zIndex based on map_id (lower map_id gets higher zIndex)
				marker.setZIndex(1000 - place.map_id);

				marker.addEventListener('tap', () => {
					if (place && placeRefs.current) {
						const placeRef = placeRefs.current[place.id];
						if (placeRef) {
							placeRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}
						selectedPlaceRef.current = place;
						setSelectedPlace(place);

						const bounds = calculateBoundingBox(allCoordinates);
						const mapContainer = mapRef.current;
						const zoomLevel = calculateZoomLevel(bounds, mapContainer.offsetWidth, mapContainer.offsetHeight);

						const currentZoom = map.getZoom();
						const zoomTolerance = 2; // Define a tolerance value

						console.log('createHubAndGemMarkers: currentZoom=' + currentZoom + ' setting new zoom=' + zoomLevel);
					}
				});

				map.addObject(marker);
				return marker;

			}));

			console.log('createHubAndGemMarkers adding markers ' + newMarkers.length);
			hubMarkersRef.current = newMarkers;
		}
	};

	function createPinIconSvgTaperedWithText(
		fillColor: string,
		strokeColor: string,
		label?: number,
		strokeWidth = 1.25,
		width = 34,
		height = 34,
		textConfig = { fontSize: 13, bold: true }
	): H.map.Icon {
		const svg = `
    <svg
      width="${width}"
      height="${height}"
      viewBox="0 0 36 56"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Pin shape -->
      <path
        d="
          M18 2
          C10 2 6 9 6 17
          C6 24 11 30 14 35
          C16 39 17 44 18 54
          C19 44 20 39 22 35
          C25 30 30 24 30 17
          C30 9 26 2 18 2
          Z
        "
        fill="${fillColor}"
        stroke="${strokeColor}"
        stroke-width="${strokeWidth}"
      />

      <!-- Highlight -->
      <ellipse
        cx="13"
        cy="12"
        rx="4"
        ry="3"
        fill="rgba(255,255,255,0.25)"
      />

      ${label !== undefined
				? `
        <text
          x="18"
          y="19"
          text-anchor="middle"
          dominant-baseline="middle"
          font-family="Arial, sans-serif"
		  font-size="${textConfig.fontSize}"
          font-weight="${textConfig.bold ? 'bold' : 'normal'}"
          fill="#000"
        >
          ${label}
        </text>
      `
				: ""
			}
    </svg>
  `;

		return new H.map.Icon(
			`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
			{
				size: { w: width, h: height },
				anchor: { x: width / 2, y: height }
			}
		);
	}


	function createHubPinIconSvgTapered(
		fillColor: string,
		strokeColor: string,
		strokeWidth = 1.25,
		width = 36,
		height = 56
	): H.map.Icon {
		const svg = `
    <svg
      width="${width}"
      height="${height}"
      viewBox="0 0 36 56"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="
          M18 2
          C10 2 6 9 6 17
          C6 24 11 30 14 35
          C16 39 17 44 18 54
          C19 44 20 39 22 35
          C25 30 30 24 30 17
          C30 9 26 2 18 2
          Z
        "
        fill="${fillColor}"
        stroke="${strokeColor}"
        stroke-width="${strokeWidth}"
      />
      <ellipse
        cx="13"
        cy="12"
        rx="4"
        ry="3"
        fill="rgba(255,255,255,0.25)"
      />
    </svg>
  `;

		return new H.map.Icon(
			`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
			{
				size: { w: width, h: height },
				anchor: { x: width / 2, y: height }
			}
		);
	}

	type HubPinTextOptions = {
		fontSize?: number;
		bold?: boolean;
	};

	function createHubPinIconSvgWithText(
		mapId: number,
		fillColor = "#00e64d",
		strokeColor = "#0a7a35",
		strokeWidth = 1.25,
		width = 36,
		height = 52,
		textOptions: HubPinTextOptions = {}
	): H.map.Icon {
		const {
			fontSize = 14,
			bold = false
		} = textOptions;

		const fontWeight = bold ? "bold" : "normal";

		const svg = `
    <svg
      width="${width}"
      height="${height}"
      viewBox="0 0 36 52"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Pin body -->
      <path
        d="
          M18 2
          C9.7 2 3 8.7 3 17
          C3 28.5 18 50 18 50
          C18 50 33 28.5 33 17
          C33 8.7 26.3 2 18 2
          Z
        "
        fill="${fillColor}"
        stroke="${strokeColor}"
        stroke-width="${strokeWidth}"
      />

      <!-- Highlight -->
      <ellipse
        cx="13"
        cy="12"
        rx="4"
        ry="3"
        fill="rgba(255,255,255,0.25)"
      />

      <!-- map_id text -->
      <text
        x="18"
        y="20"
        text-anchor="middle"
        dominant-baseline="middle"
        font-family="Arial, sans-serif"
        font-size="${fontSize}"
        font-weight="${fontWeight}"
        fill="#000"
      >
        ${mapId}
      </text>
    </svg>
  `;

		return new H.map.Icon(
			`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
			{
				size: { w: width, h: height },
				anchor: { x: width / 2, y: height }
			}
		);
	}


	function createHubPinIconSvg(
		fillColor = "#00e64d",
		strokeColor = "#0a7a35",
		strokeWidth = 1.25,
		width = 36,
		height = 52
	): H.map.Icon {
		const svg = `
    <svg
      width="${width}"
      height="${height}"
      viewBox="0 0 36 52"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="
          M18 2
          C9.7 2 3 8.7 3 17
          C3 28.5 18 50 18 50
          C18 50 33 28.5 33 17
          C33 8.7 26.3 2 18 2
          Z
        "
        fill="${fillColor}"
        stroke="${strokeColor}"
        stroke-width="${strokeWidth}"
      />
      <ellipse
        cx="13"
        cy="12"
        rx="4"
        ry="3"
        fill="rgba(255,255,255,0.25)"
      />
    </svg>
  `;

		return new H.map.Icon(
			`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
			{
				size: { w: width, h: height },
				anchor: { x: width / 2, y: height }
			}
		);
	}


	function createHubPinIcon(
		width = 36,
		height = 52,
		fillColor = "#00e64d",
		strokeColor = "#0a7a35",
		strokeWidth = 1.5
	): Promise<H.map.Icon> {
		return new Promise((resolve) => {
			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;

			const ctx = canvas.getContext("2d");
			if (!ctx) return;

			const centerX = width / 2;
			const headRadius = width * 0.35;
			const headCenterY = headRadius + strokeWidth;
			const tipY = height - strokeWidth;

			ctx.clearRect(0, 0, width, height);

			ctx.beginPath();

			// Head (perfect circle)
			ctx.arc(centerX, headCenterY, headRadius, Math.PI * 1.05, Math.PI * -0.05, false);

			// Right side taper
			ctx.bezierCurveTo(
				centerX + headRadius * 0.95,
				headCenterY + headRadius * 0.9,
				centerX + headRadius * 0.55,
				height * 0.72,
				centerX,
				tipY
			);

			// Left side taper
			ctx.bezierCurveTo(
				centerX - headRadius * 0.55,
				height * 0.72,
				centerX - headRadius * 0.95,
				headCenterY + headRadius * 0.9,
				centerX - headRadius * 0.95,
				headCenterY
			);

			ctx.closePath();

			// Fill
			ctx.fillStyle = fillColor;
			ctx.fill();

			// Thin stroke
			ctx.strokeStyle = strokeColor;
			ctx.lineWidth = strokeWidth;
			ctx.stroke();

			// Subtle highlight on head
			ctx.beginPath();
			ctx.ellipse(
				centerX - headRadius * 0.35,
				headCenterY - headRadius * 0.35,
				headRadius * 0.35,
				headRadius * 0.25,
				-0.5,
				0,
				Math.PI * 2
			);
			ctx.fillStyle = "rgba(255,255,255,0.25)";
			ctx.fill();

			const icon = new H.map.Icon(canvas.toDataURL(), {
				size: { w: width, h: height },
				anchor: { x: centerX, y: tipY }
			});

			resolve(icon);
		});
	}



	function createIconWithText(
		number: number,
		width = 30,
		height = 30,
		baseIconSrc = '/green-dot.png',
		circleColor?: string
	): Promise<H.map.Icon> {
		return new Promise((resolve) => {
			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;
			const ctx = canvas.getContext("2d");

			const img = new Image();
			img.crossOrigin = "anonymous";
			img.src = baseIconSrc;
			img.onload = () => {
				ctx.drawImage(img, 0, 0, width, height);

				// Circle behind text
				ctx.beginPath();
				ctx.arc(width / 2, height / 2 - 6, 6, 0, 2 * Math.PI);

				// Choose fill colour: explicit param > legacy rule > default
				if (circleColor) {
					ctx.fillStyle = circleColor;
				}
				ctx.fill();

				// Overlay text
				log.fillStyle = "black";
				ctx.font = "9px Arial";
				ctx.textAlign = "center";
				ctx.textBaseline = "middle";
				ctx.fillText(String(number), width / 2, height / 2 - 5);

				const icon = new H.map.Icon(canvas.toDataURL(), {
					size: { w: width, h: height },
				});
				resolve(icon);
			};
		});
	}

	const validateEventsAndCreateEventMarkers = async () => {

		const uniqueEventLocations = getUniqueEventLocations(events);

		assignEventVenueMapIds(uniqueEventLocations);

		setEventVenues(uniqueEventLocations);

		if (showPlaceMarkersRef.current) {

			clearEventMarkers();

			await createNewEventMarkers(uniqueEventLocations);
		}
	}

	const createNewEventMarkers = async (uniqueEventLocations) => {
		if (uniqueEventLocations.length > 0 && mapInstance.current) {
			const map = mapInstance.current;

			const allCoordinates = [];

			// Create markers for unique positions if they don't already exist
			const newMarkers = await Promise.all(uniqueEventLocations.map(async (location) => {

				const lat = Number(location.position.lat);
				const lng = Number(location.position.lng);


				// Check if a marker already exists at this position
				const existingMarker = eventMarkersRef.current.find(marker =>
					marker.originalLat === lat && marker.originalLng === lng
				);

				if (existingMarker) {
					return existingMarker; // Return the existing marker
				}

				const icon = createPinIconSvgTaperedWithText(
					"#16B0F9",
					"#0b6fa8",
					location.map_id,
					1.25,
					26,
					32
				);

				const marker = new H.map.Marker(
					{ lat, lng },
					{
						icon: icon,
						data: location.title,
					}
				);

				// Store original latitude and longitude
				marker.originalLat = lat;
				marker.originalLng = lng;


				allCoordinates.push({ lat: lat, lng: lng });

				marker.addEventListener('tap', () => {
					/*
					const matchingEvent = events.find(event => 
						Math.abs(event.position.lat - lat) < TOLERANCE && 
						Math.abs(event.position.lng - lng) < TOLERANCE && 
						event.isEvent
					);
					if (matchingEvent && eventRefs.current) {
						const eventRef = eventRefs.current[matchingEvent.id];
						if (eventRef) {
							eventRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}
						selectedEventRef.current = matchingEvent;
						setSelectedEvent(matchingEvent);
					}
					centerMapOnMarker(marker);
					*/
					if (location && placeRefs.current) {
						const placeRef = placeRefs.current[location.id];
						if (placeRef) {
							placeRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}
						selectedPlaceRef.current = location;
						setSelectedPlace(location);

						const bounds = calculateBoundingBox(allCoordinates);
						const mapContainer = mapRef.current;
						const zoomLevel = calculateZoomLevel(bounds, mapContainer.offsetWidth, mapContainer.offsetHeight);

						const currentZoom = map.getZoom();
						const zoomTolerance = 2; // Define a tolerance value

						console.log('createMarkers: currentZoom=' + currentZoom + ' setting new zoom=' + zoomLevel);
						if (Math.abs(currentZoom - zoomLevel) > zoomTolerance) {

							map.setZoom(zoomLevel);
							centerMapOnMarker(marker);
						}
					}
				});

				map.addObject(marker);
				return marker;
			}));

			eventMarkersRef.current = [...eventMarkersRef.current, ...newMarkers];
		}
	};

	useEffect(() => {

		const runTasks = async () => {
			const updatedPlaces = [...places]; // Create a copy 
			assignMapIds(updatedPlaces);
			setPlacesWithMapIds(updatedPlaces);

			updateDisplayedMarkers();

			checkAndHandleInitLoadSelectedPlace();
		};

		runTasks();

	}, [places]);

	async function ensurePlaceMarkersVisible() {
		if (showPlaceMarkersRef.current) return;

		showPlaceMarkersRef.current = true;

		if (places.length > 0 && mapInstance.current) {
			await createMarkers();
			await validateEventsAndCreateEventMarkers();
		}

	}

	async function updateDisplayedMarkers() {

		if (places.length > 0 && mapInstance.current) {
			clearMarkers();
			clearEventMarkers();
		}

		if (showHubMarkersRef.current) {
			await createHubAndGemMarkers();
		}

		if (showPlaceMarkersRef.current) {
			await createMarkers();
			await validateEventsAndCreateEventMarkers();
		}
	}

	useEffect(() => {

		const runTasks = async () => {
			validateEventsAndCreateEventMarkers();
		};

		runTasks();

	}, [events]);


	const getUniqueEventLocations = (events) => {
		const uniqueLocations = [];

		events.forEach(event => {
			if (event.isEvent) {
				const existingLocation = uniqueLocations.find(location =>
					Math.abs(location.position.lat - event.position.lat) < TOLERANCE &&
					Math.abs(location.position.lng - event.position.lng) < TOLERANCE
				);

				if (!existingLocation) {
					// Determine the title based on the address
					let title = event.address;
					if (event.address && event.address.includes(',')) {
						title = event.address.split(',')[0];
					} else if (event.address) {
						title = event.address;
					} else {
						title = '';
					}


					uniqueLocations.push({
						id: event.id,
						place_id: event.place_id,
						title: title,
						address: event.address,
						position: event.position,
						categories: [{ name: 'Event Venue' }],
						map_id: event.map_id,
						placeType: 'Event Venue',
						isEvent: true
					});
				}
			}
		});

		return uniqueLocations;
	};


	useEffect(() => {
		if (!midpoint || !mapInstance.current || !mapRef.current) return;

		const map = mapInstance.current;
		map.setCenter(midpoint);

		// Clear midpoint markers and routes
		midpointMarkers.current.forEach(marker => map.removeObject(marker));
		midpointMarkers.current = [];

		routeRenderers.current.forEach(renderer => map.removeObject(renderer));
		routeRenderers.current = [];

		if (!routes || routes.length === 0) {
			throw new Error('No route found between the specified locations.');
		}

		const allCoordinates: { lat: number; lng: number }[] = [];

		// Render routes with correct layering
		renderRoutes(map, mainRouteIndex, allCoordinates);

		// ---- Midpoint marker (unchanged) ----

		const midPointMarkerIcon = createHubPinIconSvgWithText(
			'½',
			'#F7CD07',
			'#F4AD16',
			1.25,
			36,
			52,
			{ fontSize: 16, bold: true }
		);

		const midPointMarker = new H.map.Marker(midpoint, {
			icon: midPointMarkerIcon,
			data: 'HALFWAY'
		});

		let midPointLabelText = '';
		if (SHOW_MIDPOINT_LABEL) {
			midPointLabelText = 'Halfway';
		}

		const midPointLabel = new H.map.DomMarker(midpoint, {
			icon: new H.map.DomIcon(
				`<div style={{color:#121212; background-color: transparent; font-size: 16px; text-align: center; width: 100px; margin-left: -50px;">
        ${midPointLabelText}
      </div>`
			)
		});

		midPointMarker.setZIndex(1500);

		map.addObject(midPointMarker);
		map.addObject(midPointLabel);

		midpointMarkers.current.push(midPointMarker, midPointLabel);

		// ---- Zoom & bounds ----

		const bounds = calculateBoundingBox(allCoordinates);
		const mapContainer = mapRef.current;
		const zoomLevel = calculateZoomLevel(
			bounds,
			mapContainer.offsetWidth,
			mapContainer.offsetHeight
		);

		map.setZoom(zoomLevel);
		setMapBoundingBox(bounds);
	}, [midpoint]);


	const buildLineStringFromRoute = (
		route: Route,
		collectCoordinates?: (coord: { lat: number; lng: number }) => void
	) => {
		const lineString = new H.geo.LineString();

		route.sections.forEach(section => {
			const polyline = section.polyline;

			if (typeof polyline === 'string') {
				const decoded = decode(polyline).polyline;
				decoded.forEach(([lat, lng]) => {
					lineString.pushLatLngAlt(lat, lng, 0);
					collectCoordinates?.({ lat, lng });
				});
			} else if (Array.isArray(polyline)) {
				polyline.forEach(point => {
					lineString.pushLatLngAlt(point.lat, point.lng, 0);
					collectCoordinates?.({ lat: point.lat, lng: point.lng });
				});
			}
		});

		return lineString;
	};

	const drawRoute = ({
		map,
		route,
		index,
		isMain,
		onTap,
		collectCoordinates
	}: {
		map: H.Map;
		route: Route;
		index: number;
		isMain: boolean;
		onTap: () => void;
		collectCoordinates?: (coord: { lat: number; lng: number }) => void;
	}) => {
		const lineString = buildLineStringFromRoute(route, collectCoordinates);

		// Optional casing for main route
		if (isMain) {
			const casing = new H.map.Polyline(lineString, {
				style: {
					lineWidth: 5,
					strokeColor: '#ffffff',
					zIndex: 9
				}
			});

			map.addObject(casing);
			routeRenderers.current.push(casing);
		}

		const polyline = new H.map.Polyline(lineString, {
			style: {
				lineWidth: isMain ? 4 : 3,
				strokeColor: isMain ? 'deepskyblue' : 'lightslategray',
				lineTailCap: 'arrow-tail',
				lineHeadCap: 'arrow-head',
				zIndex: isMain ? 10 : 1
			}
		});

		polyline.addEventListener('tap', onTap);

		map.addObject(polyline);
		routeRenderers.current.push(polyline);
	};

	const renderRoutes = (map: H.Map, mainIndex: number, allCoordinates: any[]) => {
		// Clear existing routes
		map.removeObjects(routeRenderers.current);
		routeRenderers.current = [];

		// Draw alternatives first
		routes.forEach((route, idx) => {
			if (idx !== mainIndex) {
				drawRoute({
					map,
					route,
					index: idx,
					isMain: false,
					onTap: () => {
						setMainRouteIndex(idx);
						setChangeSource('route');
					},
					collectCoordinates: coord => allCoordinates.push(coord)
				});
			}
		});

		// Draw main route last (on top)
		drawRoute({
			map,
			route: routes[mainIndex],
			index: mainIndex,
			isMain: true,
			onTap: () => { },
			collectCoordinates: coord => allCoordinates.push(coord)
		});
	};


	const smoothScrollTo = (target, duration = 1000) => {
		console.log('Scrolling to:', target);
		const start = window.pageYOffset;
		const end = target.getBoundingClientRect().top + start;
		const startTime = performance.now();

		const scroll = (currentTime) => {
			const timeElapsed = currentTime - startTime;
			const progress = Math.min(timeElapsed / duration, 1);
			const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
			window.scrollTo(0, start + (end - start) * ease);

			if (timeElapsed < duration) {
				requestAnimationFrame(scroll);
			}
		};

		requestAnimationFrame(scroll);
	};

	// Calculate bounding box
	const calculateBoundingBox = (coordinates) => {
		let minLat = Number.POSITIVE_INFINITY;
		let maxLat = Number.NEGATIVE_INFINITY;
		let minLng = Number.POSITIVE_INFINITY;
		let maxLng = Number.NEGATIVE_INFINITY;

		coordinates.forEach(coord => {
			if (coord.lat < minLat) minLat = coord.lat;
			if (coord.lat > maxLat) maxLat = coord.lat;
			if (coord.lng < minLng) minLng = coord.lng;
			if (coord.lng > maxLng) maxLng = coord.lng;
		});

		return { minLat, maxLat, minLng, maxLng };
	};

	// Calculate zoom level
	const calculateZoomLevel = (bounds, mapWidth, mapHeight) => {
		const WORLD_DIM = { height: 256, width: 256 };
		const ZOOM_MAX = 21;

		const latRad = (lat) => {
			const sin = Math.sin(lat * Math.PI / 180);
			const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
			return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
		};

		const latFraction = (latRad(bounds.maxLat) - latRad(bounds.minLat)) / Math.PI;
		const lngDiff = bounds.maxLng - bounds.minLng;
		const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

		console.log('latFraction:', latFraction);
		console.log('lngFraction:', lngFraction);

		const latZoom = Math.floor(Math.log(mapHeight / WORLD_DIM.height / latFraction) / Math.LN2);
		const lngZoom = Math.floor(Math.log(mapWidth / WORLD_DIM.width / lngFraction) / Math.LN2);

		console.log('latZoom:', latZoom);
		console.log('lngZoom:', lngZoom);

		return Math.min(latZoom, lngZoom, ZOOM_MAX);
	};

	const getCoordinates = async (loc: string): Promise<LatLng> => {
		const response = await axios.get(`/heregeo/v1/geocode?q=${loc}&apiKey=${hereApiKey}`);
		const data = response.data;
		if (data.items.length > 0) {
			return data.items[0].position;
		} else {
			throw new Error('No results found for location: ' + loc);
		}
	};

	async function fetchCategoryPlaces(category: keyof typeof categoryQueries, location: LatLng, radiusMetres: number, hereApiKey: string) {
		const { queries, types } = hereCategoryTypesAndQueries[category];

		const requests = queries.map((q, i) =>
			axios.get(`/herediscover/v1/discover`, {
				params: {
					q,
					in: `circle:${location.lat},${location.lng};r=${radiusMetres}`,
					limit: 8,
					apiKey: hereApiKey
				}
			}).then(res =>
				(res.data.items || []).map((item: any) => ({
					...item,
					placeType: types[i],          // pick the matching type
					icon: markerIcons[types[i]]   // marker for that type
				}))
			)
		);

		const results = await Promise.all(requests);
		return results.flat();
	}

	// REFACTOR: Including rename. This does far more than just get places at a location. 
	// - runs a google image search
	// - finds ticketmaster events nearby
	// - filters hubs on route (should be changed to corridor or re prioritised near corridor)
	// - finds highway places on route
	// - deduplicates places and images
	// - builds gallery state
	const findNearbyPlaces = async (location: LatLng, route?: Route): Promise<void> => {
		console.log('FNP start');
		setPlaces([]);
		setEvents([]);

		//console.log('FNP called and selected types ' + { hereCategories });
		console.info(selectedTypes);
		const radiusMetres = radius * 1000;
		let requests: Promise<any[]>[] = [];

		// In aiMode there is custom user Intent not a category such as Eat & Drink. but in standard non-ai mode
		// There is only the Eat & Drink category here maps api can recognise. 

		if (!hideHerePlaces && selectedTypes.includes('Eat & Drink')) {
			requests.push(fetchCategoryPlaces('Eat & Drink', location, radiusMetres, hereApiKey));
		}

		const results = (await Promise.all(requests)).flat();

		// List of keywords to filter out places
		let excludedPlaceNames = ['Little Chef'];
		let excludedEatAndDrinkCategoryNames = ['Rental and Leasing', 'Butcher', 'Library', 'Take Out & Delivery Only', 'Gas Station', 'Training & Development', 'Fast Food', 'Casual Dining', 'Organizations and Societies', 'Grocery', 'Department Store'];


		// Step 1: Filter out bad place names
		let filteredPlaces = results.filter(
			(place: any) => !excludedPlaceNames.some(keyword => place.title.includes(keyword))
		);

		// Step 2: Additional filter — only apply for Eat & Drink searches
		if (selectedTypes.includes('Eat & Drink')) {
			filteredPlaces = filteredPlaces.filter(
				(place: any) => {
					const categoryName = place.categories?.[0]?.name || '';
					return !excludedEatAndDrinkCategoryNames.some(
						(excluded) => categoryName.toLowerCase() === excluded.toLowerCase()
					);
				}
			);
		}

		// Step 3: Limit number of places returned
		filteredPlaces = filteredPlaces.slice(0, 60);

		let detailCalls = 0;

		// REFACTOR: I believe this was for the Google maps api version. where if more detail was accessed on the Place it would cost more
		let detailLimitedPlaceNames = [];
		// Map through filtered places and set limit_detail flag. don't show details for more than 3 items due to cost of detail request
		const policyAppliedPlaces = filteredPlaces.map((place: any, index: number) => {
			const limitDetail = detailLimitedPlaceNames.some(keyword => place.title.includes(keyword))
			return {
				...place,
				limit_detail: limitDetail,
				address: place.address.label,
				state: place.address.state,
				city: place.address.city,
				countryName: place.address.countryName,
				categories: place.categories
			};
		});

		const ticketmasterAttractions = hideEvents ? [] : await fetchAndSortTicketmasterAttractions(location);

		// Fetch details only for places without the limit_detail flag

		const detailedPlaces = await Promise.all(policyAppliedPlaces.map(async (place: Place) => {
			place.place_id = place.id;
			console.log('found place: ' + place.title);
			return place;
		}));

		const filteredHighwayPlaces = getFilteredHighwayPlaces(route, highwayPlaces, location);

		const filteredHubPlaces = hideHubs ? [] : getFilteredHubPlaces(hubPlaces, location, route);
		const filteredCuratedPicks = hideHubs ? [] : getFilteredCuratedPicks(curatedPicks, location, route);

		await addUniquePlaces([...filteredHubPlaces, ...filteredCuratedPicks], filteredHighwayPlaces, detailedPlaces);

		const categoryImages = searchType !== 'ai'
			? await fetchCategoryImages(detailedPlaces, filteredHubPlaces)
			: [];

		const newHubPhotos = extractHubPhotos(filteredHubPlaces);

		const featuredHubPhotos = newHubPhotos.slice(0, 3);
		const remainingHubPhotos = newHubPhotos.slice(3);

		const { featured, remaining } = redistributeArticleBodies(
			featuredHubPhotos,
			remainingHubPhotos
		);

		setFeaturedHubPhotos(featured);

		const dedupedHubPhotos = deduplicatePhotos(remaining);
		const dedupedCategoryPhotos = deduplicatePhotos(categoryImages);

		const galleryBatch: GalleryItem[] = [];

		if (dedupedHubPhotos.length > 0 || filteredCuratedPicks.length > 0) {
			galleryBatch.push({ type: 'SECTION_DIVIDER', id: 'divider-curated', label: 'Our Picks · Curated Places' });
			filteredCuratedPicks.forEach(pick =>
				galleryBatch.push({ type: 'CURATED_PICK', id: `curated-${pick.place_id}`, data: pick })
			);
			dedupedHubPhotos.forEach(img => galleryBatch.push({ type: 'PHOTO', id: `photo-${img.link}`, data: img }));
		}

		if (dedupedCategoryPhotos.length > 0) {
			galleryBatch.push({ type: 'SECTION_DIVIDER', id: 'divider-web-search', label: 'Web Search Results' });
			dedupedCategoryPhotos.forEach(img => galleryBatch.push({ type: 'PHOTO', id: `photo-${img.link}`, data: img }));
		}

		addUniqueItemsToGallery(galleryBatch);

		setEvents(ticketmasterAttractions);
		console.log('FNP end');
	};

	const fetchAndSortTicketmasterAttractions = async (
		location: { lat: number; lng: number }
	): Promise<TicketmasterEvent[]> => {
		const ticketmasterAttractions = await fetchTicketmasterAttractions(location);

		// Guard clause for empty results
		if (!ticketmasterAttractions || ticketmasterAttractions.length === 0) {
			return [];
		}

		// Exact sorting algorithm from your tested code
		ticketmasterAttractions.sort((a, b) => {
			const aType = a.eventType?.trim() || '';
			const bType = b.eventType?.trim() || '';

			const isAEmpty = aType === '';
			const isBEmpty = bType === '';
			const isASports = aType === 'Sports';
			const isBSports = bType === 'Sports';

			if (isAEmpty && !isBEmpty) return 1; // a is empty, b is not → a goes after
			if (!isAEmpty && isBEmpty) return -1; // b is empty, a is not → b goes after

			if (isASports && !isBSports) return 1; // a is sports, b is not → a goes after
			if (!isASports && isBSports) return -1; // b is sports, a is not → b goes after

			return 0; // preserve original order otherwise
		});

		return ticketmasterAttractions;
	};

	const getFilteredCuratedPicks = (
		picks: Place[],
		location: { lat: number; lng: number },
		route?: Route
	): Place[] => {
		if (picks.length === 0) return [];
		if (route) {
			const routeCoordinates = extractMainRouteCoordinates(route);
			return filterPlacesAlongRoute(picks, routeCoordinates, 13);
		}
		return filterHubsNearLocation(location, picks, 13);
	};

	const getFilteredHubPlaces = (
		hubPlaces: Place[],
		location: { lat: number; lng: number },
		route?: Route
	): Place[] => {
		// 1. Guard clause: Return empty if no hubs exist
		if (hubPlaces.length === 0) return [];

		// 2. Preparation: Sort and Flatten
		const sortedGroups = sortHubsByProximity(hubPlaces, location);
		const allHubPlaces = flattenHubGroups(sortedGroups);

		// 3. Conditional Filtering Logic
		if (route) {
			const routeCoordinates = extractMainRouteCoordinates(route);
			// Filter hubs along the 13-mile route corridor
			return filterPlacesAlongRoute(allHubPlaces, routeCoordinates, 13);
		}

		// Fallback: Filter hubs near the current location (13-mile radius)
		return filterHubsNearLocation(location, allHubPlaces, 13);
	};

	const getFilteredHighwayPlaces = (
		route: Route | undefined,
		highwayPlaces: Place[], // Reusing the name is safe and semantic
		location: { lat: number; lng: number }
	): Place[] => {
		// 1. Guard clause: Return empty if dependencies aren't met
		if (!route || highwayPlaces.length === 0) {
			return [];
		}

		const routeCoordinates = extractMainRouteCoordinates(route);

		// 2. Filter places that lie along the route corridor (1 mile buffer)
		const alongRoutePlaces = filterPlacesAlongRoute(highwayPlaces, routeCoordinates, 1);

		// 3. Handle Midpoint Logic
		if (FILTER_HIGHWAY_PLACES_NEAR_MIDPOINT) {
			console.log("Filtering highway places near midpoint...");

			return alongRoutePlaces.filter((place: Place) => {

				const pLat = place.latitude ?? (place as any).position?.lat;
				const pLng = place.longitude ?? (place as any).position?.lng;

				// If we can't find coordinates, we shouldn't show the place
				if (pLat === undefined || pLng === undefined) {
					console.warn(`Missing coordinates for: ${place.title}`);
					return false;
				}

				const distance = calculateHaversineDistance(
					location.lat,
					location.lng,
					pLat,
					pLng,
					'M'
				);
				return distance <= HIGHWAY_MIDPOINT_RADIUS_MILES;
			});
		}

		console.log("Showing all highway places along route...");
		return alongRoutePlaces;
	};

	const addUniquePlaces = (
		filteredHubPlaces: Place[],
		filteredHighwayPlaces: Place[],
		detailedPlaces: Place[]
	) => {
		// 1. Logs as requested
		console.log(`addUniquePlaces - filteredHubPlaces ${filteredHubPlaces.length} places`);
		console.log(`addUniquePlaces - filteredHighwayPlaces ${filteredHighwayPlaces.length} places`);
		console.log(`addUniquePlaces - detailedPlaces ${detailedPlaces.length} places`);

		// 2. Combine all sources
		const combined = [...filteredHubPlaces, ...detailedPlaces, ...filteredHighwayPlaces];

		// 3. Deduplicate by place_id using a Map
		const deduped = Array.from(
			new Map(combined.map(p => [p.place_id, p])).values()
		);

		// 4. Sort: Hubs (is_hub === true) move to the top
		const sorted = deduped.sort((a, b) => {
			const aHub = a.is_hub ? 1 : 0;
			const bHub = b.is_hub ? 1 : 0;
			return bHub - aHub;
		});

		// 5. Update state
		setPlaces(sorted);
	};

	const addUniqueItemsToGallery = (incomingItems: GalleryItem[]) => {
		setGalleryItems(prev => {
			const seenInIncoming = new Set();
			const uniqueIncoming = incomingItems.filter(item => {
				if (seenInIncoming.has(item.id)) return false;
				seenInIncoming.add(item.id);
				return true;
			});

			// Filter out items that are already sitting in  state
			const currentIds = new Set(prev.map(item => item.id));
			const finalNewItems = uniqueIncoming.filter(item => !currentIds.has(item.id));

			// 4. Return the new combined deck
			return [...finalNewItems, ...prev];
		});
	};

	type RedistributeResult = {
		featured: Photo[];
		remaining: Photo[];
	};

	const redistributeArticleBodies = (
		featured: Photo[],
		remaining: Photo[]
	): RedistributeResult => {
		// Collect article bodies from featured photos
		const articleBodies = featured
			.map(photo => photo.article_body)
			.filter((body): body is string => Boolean(body && body.trim()));

		// Remove article bodies from featured photos
		const cleanedFeatured = featured.map(photo => ({
			...photo,
			article_body: undefined
		}));

		// Reassign article bodies to remaining photos that don't have one
		let articleIndex = 0;
		const updatedRemaining = remaining.map(photo => {
			if (
				!photo.article_body &&
				articleBodies[articleIndex]
			) {
				const updatedPhoto = {
					...photo,
					article_body: articleBodies[articleIndex]
				};
				articleIndex++;
				return updatedPhoto;
			}
			return photo;
		});

		return {
			featured: cleanedFeatured,
			remaining: updatedRemaining
		};
	};


	const deduplicateByPlaceIdPreservingOrder = (places: Place[]): Place[] => {
		const seen = new Set<string>();
		const result: Place[] = [];

		for (const place of places) {
			if (!seen.has(place.place_id)) {
				seen.add(place.place_id);
				result.push(place);
			}
		}

		return result;
	};


	const extractHubPhotos = (places: Place[]): Photo[] => {
		const photoList: Photo[] = [];

		places.forEach(place => {
			place.images?.forEach(img => {
				photoList.push({
					link: img.image_url,
					contextLink: img.original_source_url || '',
					title: img.caption,
					snippet: img.alt_text,
					width: 800,
					height: 600,
					orientation: 'landscape',
					is_hub_hero: img.is_hub_hero || false,
					fillColor: place.fillColor ?? DEFAULT_HUB_COLORS.fillColor,
					strokeColor: place.strokeColor ?? DEFAULT_HUB_COLORS.strokeColor,
					textColor: place.textColor ?? DEFAULT_HUB_COLORS.textColor,
					attribution: img.attribution || '',
					article_body: normaliseAndSecureArticleBody(img.article_body)
				});
			});
		});

		return photoList;
	};

	const normaliseAndSecureArticleBody = (body?: string): string | undefined => {
		if (!body) return undefined;

		const trimmed = body.trim();
		if (!trimmed) return undefined;

		return sanitiseBasicHtml(trimmed);
	};

	const sanitiseBasicHtml = (html: string): string => {
		// Strip script tags
		let safe = html.replace(
			/<script[\s\S]*?>[\s\S]*?<\/script>/gi,
			''
		);

		// Strip inline event handlers (onclick, onload, etc.)
		safe = safe.replace(/\son\w+="[^"]*"/gi, '');

		return safe;
	};


	const deduplicatePhotos = (photos: Photo[]): Photo[] => {
		const seen = new Set<string>();
		const result: Photo[] = [];

		for (const photo of photos) {
			if (!seen.has(photo.link)) {
				seen.add(photo.link);
				result.push(photo);
			}
		}

		return result;
	};

	const flattenHubPlaces = (hubs: Place[]): Place[] => {
		return hubs.flatMap(hub => [hub, ...(hub.child_places || [])]);
	};

	const deduplicatePlaces = (places: Place[]): Place[] => {
		return Array.from(new Map(places.map(p => [p.place_id, p])).values());
	};

	const filterPlacesAlongRoute = (places, routeCoordinates, maxDistance) => {
		return places.filter(place => {
			return routeCoordinates.some(routePoint =>
				isWithinDistance(place.position, routePoint, maxDistance)
			);
		});
	};

	const flattenHubGroups = (groups: HubWithChildren[]): Place[] => {
		return groups.flatMap(group => [group.hub, ...group.child_places]);
	};

	const sortHubsByProximity = (hubs: HubWithChildren[], location: LatLng): HubWithChildren[] => {
		return [...hubs].sort((a, b) => {
			const distA = getDistanceInKm(a.hub.position, location);
			const distB = getDistanceInKm(b.hub.position, location);
			return distA - distB;
		});
	};

	const isWithinDistance = (point1, point2, distance) => {
		const toRad = value => (value * Math.PI) / 180;
		const R = 6371; // Radius of the Earth in km

		const dLat = toRad(point2.lat - point1.lat);
		const dLng = toRad(point2.lng - point1.lng);
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(point1.lat)) * Math.cos(toRad(point2.lat)) *
			Math.sin(dLng / 2) * Math.sin(dLng / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const distanceInKm = R * c;

		return distanceInKm <= distance;
	};

	const getDistanceInKm = (point1: LatLng, point2: LatLng): number => {
		const toRad = value => (value * Math.PI) / 180;
		const R = 6371;

		const dLat = toRad(point2.lat - point1.lat);
		const dLng = toRad(point2.lng - point1.lng);
		const a =
			Math.sin(dLat / 2) ** 2 +
			Math.cos(toRad(point1.lat)) * Math.cos(toRad(point2.lat)) *
			Math.sin(dLng / 2) ** 2;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	};


	const filterHubsNearLocation = (location: LatLng, uniquePlaces: Place[], maxDistanceKm: number): Place[] => {
		return uniquePlaces.filter(place => isWithinDistance(place.position, location, maxDistanceKm));
	};

	const extractMainRouteCoordinates = (route) => {
		const allCoordinates = [];

		route.sections.forEach(section => {
			let polyline = section.polyline;

			// Decode polyline if it's encoded
			if (typeof polyline === 'string') {
				const decodedPolyline = decode(polyline).polyline;
				decodedPolyline.forEach(point => {
					allCoordinates.push({ lat: point[0], lng: point[1] });
				});
			} else if (Array.isArray(polyline)) {
				polyline.forEach(point => {
					allCoordinates.push({ lat: point.lat, lng: point.lng });
				});
			} else {
				console.error('Polyline is not a valid format:', polyline);
			}
		});

		return allCoordinates;
	};


	// Ramer-Douglas-Peucker Algorithm
	function ramerDouglasPeucker(points, epsilon) {
		if (points.length < 3) return points;

		let dmax = 0;
		let index = 0;
		for (let i = 1; i < points.length - 1; i++) {
			const d = perpendicularDistance(points[i], points[0], points[points.length - 1]);
			if (d > dmax) {
				index = i;
				dmax = d;
			}
		}

		if (dmax >= epsilon) {
			const recResults1 = ramerDouglasPeucker(points.slice(0, index + 1), epsilon);
			const recResults2 = ramerDouglasPeucker(points.slice(index), epsilon);

			return recResults1.slice(0, recResults1.length - 1).concat(recResults2);
		} else {
			return [points[0], points[points.length - 1]];
		}
	}

	function perpendicularDistance(point, lineStart, lineEnd) {
		const x = point[0], y = point[1];
		const x1 = lineStart[0], y1 = lineStart[1];
		const x2 = lineEnd[0], y2 = lineEnd[1];

		const area = Math.abs((x1 - x2) * (y1 - y) - (x1 - x) * (y1 - y2));
		const base = Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));

		return area / base;
	}

	const fetchCategoryImages = async (places: Place[], hubPlaces: Place[]): Promise<Photo[]> => {
		// Check for hub-defined queries
		const hubWithQuery = hubPlaces.find(hub =>
			hub.is_hub && typeof hub.cse_query === 'string' && hub.cse_query.trim().length > 0
		);

		// Build query and glCode
		let query: string;
		let glCode: string = 'UK'; // default fallback

		if (hubWithQuery) {
			query = hubWithQuery.cse_query!;
			const hubCountryCode = countryNameToCode[hubWithQuery.countryName];
			if (hubCountryCode) glCode = hubCountryCode;
			console.log(`Using hub-defined query: ${query}`);
		} else {
			const uniqueCities = [...new Set(places.map(place => place.city))];
			const uniqueCountries = [...new Set(places.map(place => place.countryName))];

			query = `${queryPrefixes['Eat & Drink']} IN ${uniqueCities.join(' OR ')}`;
			console.log('IMAGE SEARCH TERMS (fallback): ' + query);

			for (const country of uniqueCountries) {
				const code = countryNameToCode[country];
				if (code) {
					glCode = code;
					break;
				}
			}
		}

		try {
			const response = await axios.get(`/imagesearch/customsearch/v1`, {
				params: {
					q: query,
					cx: customSearchEngineID,
					searchType: 'image',
					key: apiKey,
					count: 10,
					startIndex: 1,
					inputEncoding: 'utf8',
					outputEncoding: 'utf8',
					safe: 'off',
					imgSize: 'xxlarge',
					imgType: 'photo',
					filter: 1,
					exactTerms: hubWithQuery ? '' : [...new Set(places.map(p => p.state))].join(' '),
					gl: glCode
				}
			});

			const items = response.data.items || [];
			const images = items.map((item: any) => {
				const { link, image, title, snippet } = item;
				const { contextLink, width, height } = image;
				const orientation = width > height ? 'landscape' : 'portrait';
				console.log(`image w:${width} h:${height} o:${orientation} L:${link}`);

				return { link, contextLink, title, snippet, width, height, orientation };
			});

			const uniqueImages = filterUndesiredImages(images);

			return uniqueImages;
		} catch (error) {
			console.error('Error fetching images:', error);
			return [];
		}
	};



	const groupImages = (photos) => {
		const landscapeBig = [];
		const landscapeSmall = [];
		const portraitBig = [];
		const portraitSmall = [];

		photos.forEach((photo) => {
			const isBig = photo.width > 250 || photo.height > 350; // Adjust the size threshold as needed
			if (photo.orientation === 'landscape') {
				if (isBig) {
					landscapeBig.push(photo);
				} else {
					landscapeSmall.push(photo);
				}
			} else {
				if (isBig) {
					portraitBig.push(photo);
				} else {
					portraitSmall.push(photo);
				}
			}
		});

		console.log('landscapeBig' + landscapeBig.length + 'landscapeSmall' + landscapeSmall.length);
		console.log('portraitBig' + portraitBig.length + 'portraitSmall' + portraitSmall.length);
		return { landscapeBig, landscapeSmall, portraitBig, portraitSmall };
	};

	const trimTitle = (title) => {
		const delimiters = [" by ", " Reviews", " Prices", " Photos", " Updated", " Royalty-Free", " Free", " Stock", "Stock-Photos", "Stock-Images"];
		let trimmedTitle = title;

		delimiters.forEach(delimiter => {
			const index = trimmedTitle.indexOf(delimiter);
			if (index !== -1) {
				trimmedTitle = trimmedTitle.substring(0, index);
			}
		});

		return trimmedTitle.trim();
	};

	const generateHash = (str) => {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			const char = str.charCodeAt(i);
			hash = (hash << 5) - hash + char;
			hash |= 0; // Convert to 32bit integer
		}
		return hash;
	};


	const filterUndesiredImages = (images) => {
		const uniqueImages = [];
		const seenHashes = new Set();
		const seenTitles = new Set();

		images.forEach((image) => {
			// Skip if the image link contains any undesired source substring
			if (undesiredImageSources.some(substr => image.link.includes(substr))) {
				return;
			}

			const hash = generateHash(image.link);
			const trimmedTitle = trimTitle(image.title);

			// Skip if the trimmed title contains any undesired title keyword (case-insensitive)
			if (undesiredImageTitles.some(keyword =>
				trimmedTitle.toLowerCase().includes(keyword.toLowerCase())
			)) {
				return;
			}

			if (!seenHashes.has(hash) && !seenTitles.has(trimmedTitle)) {
				seenHashes.add(hash);
				seenTitles.add(trimmedTitle);
				uniqueImages.push({
					...image,
					title: trimmedTitle
				});
			}
		});

		return uniqueImages;
	};


	const hasTwoOrMoreMatchingWords = (placeTitle: string, text: string): boolean => {
		const placeWords = placeTitle.toLowerCase().split(' ');
		const textWords = text.toLowerCase().split(' ');

		let matchCount = 0;
		placeWords.forEach(word => {
			if (textWords.includes(word)) {
				matchCount++;
			}
		});

		if (matchCount >= 2) {
			console.log('FOUND a match for ' + placeTitle + 'and ' + text);
		} else {
			//console.log('NOT a match for '+placeTitle+ 'and '+text );
		}
		return matchCount >= 2;
	};

	const getPlaceDetails = async (placeId: string): Promise<Partial<Place>> => {

		try {
			const response = await axios.get(`/hereplaces/places/v1/lookup?id=${placeId}?apiKey=${hereApiKey}`);
			const data = response.data;

			return {
				id: data.id,
				place_id: data.id,
				title: data.title,
				address: data.address.label,
				position: data.position,
				averageRating: data.averageRating,
				ratingCount: data.ratingCount,
				openingHours: data.openingHours,
				reviews: data.reviews,
				photos: [], // HERE Maps does not provide photos
				show_photos: false,
				limit_detail: false,
				description: data.description || ''
			};
		} catch (error: any) {
			console.error('Error fetching place details:', error.response ? error.response.data : error.message);
			throw new Error('Failed to fetch place details');
		}
	};

	const handleFind = async () => {
		try {
			setError('');

			setInitialContentDisplayed(false);
			setGalleryItems([]);

			if (location1 && location2 && location1.length > 0 && location2.length > 0) {

				logInfo('Find run for input 1:' + location1 + ' input 2:' + location2);

				// two locations entered, so find routes, midpoint and places
				getDirectionsAndPlacesAtMidpointBetweenLocations();

			} else if (location1 && location1.length > 0) {

				logInfo('Find run for input 1:' + location1);
				// only one location entered so find places at this location
				const newFocalPoint = await getCoordinates(location1);

				if (newFocalPoint) {
					setLocation1LL(newFocalPoint);

					setFocalPoint(newFocalPoint);
				}
			} else {
				setError('no location has been entered');
			}
		} catch (error) {
			setError((error as Error).message);
		}
	};

	const getDirectionsAndPlacesAtMidpointBetweenLocations = async () => {
		console.log('getDirectionsAndPlacesAtMidpointBetweenLocations called');
		console.info('GDAPAMBL location1=' + location1);
		try {
			setError('');

			if (location1 && location2 && location1.length > 0 && location2.length > 0) {
				const loc1 = await getCoordinates(location1);
				const loc2 = await getCoordinates(location2);

				setLocation1LL(loc1);
				setLocation2LL(loc2);

				clearPlaces();
				clearMarkers();

				const directionsResponse = await axios.get(`/hereroutes/v8/routes?transportMode=car&origin=${loc1.lat},${loc1.lng}&destination=${loc2.lat},${loc2.lng}&return=polyline,summary,actions&alternatives=3&apiKey=${hereApiKey}`);
				const routes = directionsResponse.data.routes;

				console.log('Routes received:', routes);

				if (!routes || routes.length === 0) {
					throw new Error('No route found between the specified locations.');
				}

				// Sort routes by distance with additional checks
				routes.sort((a, b) => {
					const aLength = a.sections?.[0]?.summary?.length;
					const bLength = b.sections?.[0]?.summary?.length;
					if (aLength === undefined || bLength === undefined) {
						throw new Error('Route summary length is undefined.');
					}
					return aLength - bLength;
				});

				setRoutes(routes);

				// Important to avoid bugs/regression if changing
				// The default mainRouteIndex is 0 which will be the shortest distance
				// However, when a use taps another route. MainRoutesIndex changes and the tap
				// corresponds to the index order routes has been set.

				const mainRoute = routes[mainRouteIndex];

				const midpoint = calculateHereRouteMidpoint(mainRoute);
				setMidpoint(midpoint);

				await findNearbyPlaces(midpoint, mainRoute);

				// --- THE RESILIENCE RETURN ---
				// We return everything needed for the next step (AI Parse)
				return {
					routes,
					midpoint
				};

			} else {
				setError('No location has been entered');
			}
		} catch (error) {
			console.error('Error in getDirectionsAndPlacesAtMidpointBetweenLocations:', error);
			setError((error as Error).message);
		}
	};
	// end of Here dependency

	const calculateHereRouteMidpoint = (route: any): LatLng => {

		//console.log('calculateHereRouteMidpoint route.sections size:'+route.sections.length);
		if (!route || !route.sections) {
			throw new Error("Invalid route data");
		}

		const totalDistance = route.sections.reduce((sum: number, section: any) => sum + section.summary.length, 0);
		let traveledDistance = 0;
		let midpoint: LatLng = { lat: 0, lng: 0 };

		for (const section of route.sections) {
			const sectionDistance = section.summary.length;

			console.log('calculateHereRouteMidpoint section length' + sectionDistance);
			if (traveledDistance + sectionDistance >= totalDistance / 2) {
				const remainingDistance = totalDistance / 2 - traveledDistance;
				const fraction = remainingDistance / sectionDistance;

				// Decode the polyline to get a more accurate midpoint
				const polyline = decode(section.polyline).polyline;
				let polylineDistance = 0;

				for (let i = 0; i < polyline.length - 1; i++) {
					const segmentStart = polyline[i];
					const segmentEnd = polyline[i + 1];

					var segmentDistance: number = 0;
					if (Array.isArray(segmentStart) && segmentStart.length === 2 &&
						Array.isArray(segmentEnd) && segmentEnd.length === 2) {
						segmentDistance = computeDistanceBetween(segmentStart as [number, number], segmentEnd as [number, number]);

						if (polylineDistance + segmentDistance >= remainingDistance) {
							const segmentFraction = (remainingDistance - polylineDistance) / segmentDistance;
							midpoint = interpolate(segmentStart as [number, number], segmentEnd as [number, number], segmentFraction);
							break;
						}
						polylineDistance += segmentDistance;
					}

				}
				break;
			}
			traveledDistance += sectionDistance;
		}
		console.log('totalMainRouteDistance= ' + totalDistance);
		if (totalDistance && totalDistance > 0) {
			var distanceInKm = totalDistance / 1000;
			setTotalMainRouteDistance(distanceInKm);
		}

		return midpoint;
	};

	const getUrlParams = () => {
		const params = new URLSearchParams(window.location.search);
		return {
			urlLocation1: params.get('location1') || '',
			urlLocation2: params.get('location2') || '',
			routeIndex: params.get('routeIndex') || '',
			searchType: params.get('searchType') || '',
			aiPrompt: params.get('aiPrompt') || '',
			urlFilters: params.get('filters') ? params.get('filters').split(',') : [],
			urlLikedPlaces: params.get('likedPlaces') ? params.get('likedPlaces').split(',') : [],
			urlSelectedPlaceId: params.get('selectedPlaceId') || null,
			urlMode: params.get('mode') || 'classic',
			urlHideEvents: params.get('hideEvents') === '1',
			urlHideHerePlaces: params.get('hideHerePlaces') === '1',
			urlHideHubs: params.get('hideHubs') === '1',
		};
	};

	const handleTypeChange = (type: string, event) => {
		//event.stopPropagation();
		setSelectedTypes([type]);
		setIsDropdownOpen(false); // Close the drop-down
	};


	const toggleDropdown = (event) => {
		//event.stopPropagation();
		const newDropdownState = !isDropdownOpen;
		setIsDropdownOpen(newDropdownState);
	};

	const handleOverlayClick = (event) => {
		//event.stopPropagation();
		setIsDropdownOpen(false);
	};


	const clearPlaces = () => {
		setPlaces([]);
		setEvents([]);
		selectedPlaceRef.current = null;
		clearMarkers();
		console.log('Places cleared:');
		console.info(places);
	};

	const clearMarkers = () => {
		if (!mapInstance.current) return;
		const map = mapInstance.current;

		const removeMarkers = (markerArray) => {
			markerArray.forEach(marker => {
				if (marker && map.getObjects().includes(marker)) {
					map.removeObject(marker);
				}
			});
		};

		removeMarkers(markersRef.current);
		removeMarkers(hubMarkersRef.current);

		markersRef.current = [];
		hubMarkersRef.current = [];

		console.log("Markers cleared");
	};


	const clearEventMarkers = () => {
		if (eventMarkersRef.current && mapInstance.current) {
			const map = mapInstance.current;
			// Clear existing markers
			eventMarkersRef.current.forEach(marker => {
				if (map.getObjects().includes(marker)) {
					map.removeObject(marker);
				}
			});
		}
		console.log('Markers cleared:');
	};

	const handleShare = async () => {
		console.log('handleShare called');
		try {
			const url = generateShareableURL();

			logInfo('handleShare url:' + url);

			const subject = encodeURIComponent('Found Somewhere!');
			const body = encodeURIComponent(`How about: ${url}`);
			const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
			window.location.href = mailtoLink;
		} catch (error) {
			setError((error as Error).message);
		}
	};

	function generateShareableURL() {
		const params = new URLSearchParams({
			location1: location1,
			location2: location2,
			routeIndex: String(mainRouteIndex),
			searchType: searchType,
			aiPrompt: aiPrompt,
			filters: selectedTypes.join(','),
			likedPlaces: likedPlaces.join(','),
			selectedPlaceId: selectedPlace ? selectedPlace.place_id : ''
		});
		if (hideEvents) params.set('hideEvents', '1');
		if (hideHerePlaces) params.set('hideHerePlaces', '1');
		if (hideHubs) params.set('hideHubs', '1');
		return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
	}

	// Function to get  types from selected types
	const getMapTypes = (activeTypes: string[]): string => {
		// Reverse the order of activeTypes
		const reversedActiveTypes = [...activeTypes].reverse();

		// Map and join the reversed activeTypes
		return reversedActiveTypes
			.map(type => hereTypeMapping[type] || type)
			.join('|');
	};

	// Function to get HERE categories from selected types
	const getHereCategories = (activeTypes: string[]): string => {
		// Reverse the order of activeTypes
		const reversedActiveTypes = [...activeTypes].reverse();

		// Map and join the reversed activeTypes
		return reversedActiveTypes
			.map(type => hereTypeMapping[type] || type)
			.join('|');
	};

	const getHereCategoryDiscoverTerms = (activeTypes: string[]): string => {
		// Reverse the order of activeTypes 
		const reversedActiveTypes = [...activeTypes].reverse();

		// Flatten mapped discover terms and join with "|" (OR operator in HERE)
		return reversedActiveTypes
			.flatMap(type => hereCategoryDiscoverTermsMapping[type] || [])
			.join('|');
	};

	const getHereCategoryCodes = (activeTypes: string[]): string => {
		// Reverse the order of activeTypes 
		const reversedActiveTypes = [...activeTypes].reverse();

		// Flatten mapped category IDs and join as comma-separated string
		return reversedActiveTypes
			.flatMap(type => hereCategoryMapping[type] || [])
			.join(',');
	};

	const toggleReview = (placeId: string) => {
		setExpandedReviews(prevState => ({
			...prevState,
			[placeId]: !prevState[placeId]
		}));
	};

	const toggleDescription = (placeId: string) => {
		setExpandedDescriptions(prevState => ({
			...prevState,
			[placeId]: !prevState[placeId]
		}));
	};

	const fetchTicketmasterAttractions = async (location: LatLng): Promise<Place[]> => {
		const radiusKm = 30;
		const response = await axios.get(`/ticketmaster/discovery/v2/events.json?apikey=${ticketMasterApiKey}&latlong=${location.lat},${location.lng}&radius=${radiusKm}&unit=km`);
		const data = response.data;

		const eventsMap: { [key: string]: Place } = {};

		if (data._embedded) {
			data._embedded.events.forEach((event: any) => {
				try {
					const compositeKey = `${event.name}-${event._embedded.venues[0].name}`;
					if (compositeKey && !eventsMap[compositeKey]) {
						eventsMap[compositeKey] = {
							id: event.id,
							place_id: event.id,
							title: event.name,
							address: event._embedded.venues[0].name,
							position: {
								lat: event._embedded.venues[0].location.latitude,
								lng: event._embedded.venues[0].location.longitude
							},
							show_photos: false,
							limit_detail: false,
							isEvent: true,
							eventPhotoUrl: event.images?.[0]?.url ?? '',
							eventType: event.classifications?.[0]?.segment?.name ?? '',
							subGenre: event.classifications?.[0]?.subGenre?.name ?? '',
							priceRange: event.priceRanges ? `${event.priceRanges[0].min}` : 'Click to Check',
							eventDates: [],
							description: event.description,
							ticketUrl: event.url
						};
					}
					let mappedEvent = eventsMap[compositeKey];
					if (mappedEvent && event.dates && event.dates.start && event.dates.start.localDate && event.dates.start.localTime) {
						let mappedEventDates = mappedEvent.eventDates;
						if (mappedEventDates) {
							mappedEventDates.push(`${event.dates.start.localDate} ${event.dates.start.localTime}`);
						}
					} else {
						console.warn('Missing date information for event:', event);
					}
				} catch (error) {
					console.error('Error processing event:', event, error);
				}
			});
		} else {
			console.warn('No events found in response:', data);
		}

		return Object.values(eventsMap);
	};

	const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setRadiusInput(value);

		if (value === '') {
			setRadius(0);
			setErrorMessage('');
		} else if (!isNaN(Number(value)) && Number(value) >= 0) {
			setRadius(Number(value));
			setErrorMessage('');
		} else {
			setErrorMessage('Please enter a valid number.');
		}
	};

	const handleGetCurrentLocation = async () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				async (position) => {
					const { latitude, longitude } = position.coords;

					try {
						const placeName = await getHerePlaceNameFromCoords(latitude, longitude);
						setLocation1(placeName);
					} catch (error) {
						console.error('Error reverse geocoding location:', error);
					}
				},
				(error) => {
					console.error('Error getting current location:', error);
				}
			);
		} else {
			console.error('Geolocation is not supported by this browser.');
		}
	};

	const getHerePlaceNameFromCoords = async (lat: number, lng: number): Promise<string> => {
		const response = await fetch(
			`/hererevgeocode/v1/revgeocode?at=${lat},${lng}&lang=en-US&apiKey=${hereApiKey}`
		);

		if (!response.ok) {
			console.error('HERE reverse geocode failed with status:', response.status);
			const errorText = await response.text();
			console.error('Response body:', errorText);
			return 'No address found';
		}

		const data = await response.json();
		console.log('HERE reverse geocode response:', data);

		if (!data.items || data.items.length === 0) {
			console.warn('HERE returned no address matches for:', lat, lng);
			return 'No address found';
		}

		return data.items?.[0]?.address?.label || `No address found`;
	};

	useEffect(() => {
		const handleResize = () => {
			if (mapInstance.current) {
				mapInstance.current.getViewPort().resize();
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [mapInstance]);

	useEffect(() => {
		const staticCssLink = document.getElementById('static-css');
		const portraitCssLink = document.getElementById('portrait-css');
		if (staticCssLink) {
			staticCssLink.disabled = !initialContentDisplayed;
		}
		if (portraitCssLink) {
			portraitCssLink.disabled = initialContentDisplayed;
		}
	}, [initialContentDisplayed]);

	useEffect(() => {

		// SCENARIO 1: If we are NOT in AI mode, stop everything and set the Web Default
		if (searchType !== 'ai') {
			setAiPrompt(NOAI_SEARCH_DEFAULT);
			return; // Stop here, no timer needed
		}

		// SCENARIO 2: If we ARE in AI mode but the user has typed something, stop the timer
		if (aiPrompt.length > 0) {
			return;
		}

		// Change the suggestion every 3 seconds
		const interval = setInterval(() => {
			setSuggestionIndex((current) => (current + 1) % SEARCH_SUGGESTIONS.length);
		}, 6000);

		return () => clearInterval(interval);
	}, [aiPrompt, searchType]); // Only re-run if user types or changes mode 

	// Initialize with the expected structure

	const [aiData, setAiData] = useState({
		summary: '',
		inferred_road: '',
		midpoint_town: '', // Initialize this
		suggestions: []
	});

	const [isLoading, setIsLoading] = useState(false);
	const [sheetRelatedPlaces, setSheetRelatedPlaces] = useState<Place[]>([]);

	// When the AI search completes (isLoading flips false) and the detail sheet is open,
	// populate the related places section with AI_GEM results, excluding the selected place.
	useEffect(() => {
		if (!placeDetailSheet || isLoading) return;
		const gems = places
			.filter(p => p.placeType === 'AI_GEM'
				&& p.title.toLowerCase() !== placeDetailSheet.title.toLowerCase())
			.slice(0, 5);
		setSheetRelatedPlaces(gems);
	}, [isLoading, placeDetailSheet]);

	const fetchWikimediaPlaceImage = async (query) => {
		try {
			const res = await fetch(`/api/wikimedia-image?query=${encodeURIComponent(query)}`);
			const data = await res.json();
			if (!data) {
				console.warn(`[Wikimedia] No image found for: ${query}`);
				return null;
			}
			console.log(`[Wikimedia] Image found for: ${query}`);
			return data;
		} catch (err) {
			console.error('[Wikimedia] Proxy fetch failed:', err);
			return null;
		}
	};

	const getPointAtDistance = (route, fraction) => {
		// 1. Get the polyline string from the first section
		const encodedPolyline = route.sections[0].polyline;

		// 2. Decode into an array of [lat, lng]
		const coordinates = decode(encodedPolyline).polyline;

		// 3. Calculate total "index-based" distance
		// For higher precision, you could calculate actual meters between each point,
		// but for a 50-mile corridor, fractional index is usually sufficient.
		const targetIndex = Math.floor((coordinates.length - 1) * fraction);
		const point = coordinates[targetIndex];

		return { lat: point[0], lng: point[1] };
	};

	const handleAiSubmit = async () => {
		setIsLoading(true);
		setError("");

		try {

			setInitialContentDisplayed(false);
			setGalleryItems([]);

			logInfo('handleAiSubmit run location 1:' + location1 + ' location 2:' + location2 + ' aiPrompt' + aiPrompt);

			// STEP 1: Calculate Everything & Get Fresh Data
			// We expect result to contain: { routes, midpoint, hubs }
			const result = await getDirectionsAndPlacesAtMidpointBetweenLocations();

			if (!result || !result.routes || result.routes.length === 0) {
				throw new Error("No route found. Please check your locations.");
			}

			const { routes: promptRoutes, midpoint: promptMidpoint, hubs: promptHubs } = result;
			const selectedRoute = promptRoutes[mainRouteIndex];

			if (!selectedRoute) {
				throw new Error("Selected route data is unavailable.");
			}

			// STEP 2: Calculate Metrics & Corridor Bounds
			const totalMeters = selectedRoute.sections.reduce((acc, sec) => acc + (sec.summary?.length || 0), 0);
			const totalMiles = Math.round(totalMeters * 0.000621371);

			// Get the geometric 'Window of Interest' (30% to 70% of the journey)
			const start = getPointAtDistance(selectedRoute, 0.3);
			const end = getPointAtDistance(selectedRoute, 0.7);

			console.log('handleAiSubmit - Search Start' + start + ' Search End' + end);

			// Resolve corridor bounds and midpoint to named settlements to anchor the LLM geographically
			const [corridorStartPlace, corridorEndPlace, corridorMidpointPlace] = await Promise.all([
				getHerePlaceNameFromCoords(start.lat, start.lng),
				getHerePlaceNameFromCoords(end.lat, end.lng),
				getHerePlaceNameFromCoords(promptMidpoint.lat, promptMidpoint.lng),
			]);
			console.log('handleAiSubmit - corridorStartPlace:' + corridorStartPlace + ' corridorEndPlace:' + corridorEndPlace + ' corridorMidpointPlace:' + corridorMidpointPlace);

			// Map Hubs for the 'Blacklist' using the fresh data from the result
			const curatedHubsForAi = (promptHubs || []).map(p => ({
				name: p.title,
				location: p.position
			}));

			// STEP 3: Construct the Payload
			// We pass raw LL (Lat/Lng) as the source of truth for Gemini's spatial inference
			const contextData = {
				userInput: aiPrompt,
				origin: {
					address: location1,
					coords: location1LL // Using the state values set in the directions call
				},
				destination: {
					address: location2,
					coords: location2LL
				},
				totalRoutesFound: promptRoutes.length,
				routeDistanceMiles: totalMiles,
				midpointAddress: promptMidpoint, // Used for town-naming in summary
				corridorStartPlace,
				corridorEndPlace,
				corridorMidpointPlace,
				corridor: {
					midpoint: promptMidpoint,
					startBound: start,
					endBound: end
				},
				curatedHubs: curatedHubsForAi
			};

			// STEP 4: Call AI
			const response = await fetch('/api/ai-parse', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ context: contextData }),
			});

			if (!response.ok) throw new Error("The AI service is currently unavailable.");

			const data = await response.json();

			// 1. Show the text results immediately so the app feels "snappy"
			setAiData(data);

			// 2. Start fetching images in the background
			console.log("AI text received. Now hydrating images...");
			const hydratedSpots = await Promise.all(
				data.suggestions.map(async (spot) => {
					// Call the new version of the function
					const wikiResult = await fetchWikimediaPlaceImage(spot.photo_query);

					// If we got a result, spread the new fields into the spot object
					if (wikiResult) {
						return {
							...spot,
							imageUrl: wikiResult.url,
							attribution: wikiResult.attribution,
							descriptionUrl: wikiResult.descriptionUrl,
							imageTitle: wikiResult.title
						};
					}

					// Fallback if no image is found
					return { ...spot, imageUrl: null, attribution: null };
				})
			);

			// 3. Update the state again with the images
			setAiData(prev => ({
				...prev,
				suggestions: hydratedSpots
			}));

			// 1. Convert to formal Place objects
			const newAiPlaces = mapAiSuggestionsToPlaces(hydratedSpots);

			// 2. Prepend to the existing 'places' array (scope: component level)
			setPlaces(prevPlaces => [...newAiPlaces, ...prevPlaces]);

			// 3. UPDATE GALLERY (Using the created Places, not raw spots)
			const aiSuggestedItems: GalleryItem[] = newAiPlaces.map(place => ({
				type: 'AI_PLACE',
				id: place.id, // Use the Place ID
				data: place   // Now GalleryItem.data IS a Place object
			}));

			const summaryItem: GalleryItem = {
				type: 'AI_SUMMARY',
				id: 'trip-summary',
				data: {
					summary: data.summary,
					midpoint_town: data.midpoint_town,
					inferred_road: data.inferred_road
				}
			};

			const aiDivider: GalleryItem = { type: 'SECTION_DIVIDER', id: 'divider-ai', label: 'AI Suggestions · Hidden Gems' };
			addUniqueItemsToGallery([aiDivider, summaryItem, ...aiSuggestedItems]);

		} catch (err) {
			console.error("AI Submission Error:", err);
			setError(err.message || "Trip planning error. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	// A palette of perceptible blues: from Cyan to deep Royal Blue
	const gemBluePalette = [
		{ fill: '#38CEF5', stroke: '#01A2DE' }, // 0. Original Bright Sky
		{ fill: '#4D96FF', stroke: '#1976D2' }, // 1. Soft Blue (The "Classic" anchor)
		{ fill: '#00D7FF', stroke: '#0097B2' }, // 2. Electric Cyan
		{ fill: '#72FFFF', stroke: '#00B8D4' }, // 3. Ice Blue (Very light, high contrast)
		{ fill: '#5AC8FA', stroke: '#007AFF' }, // 4. Apple Sky Blue (Super clean)
		{ fill: '#80E0FF', stroke: '#00AEEF' }, // 5. Summer Blue
		{ fill: '#00B4D8', stroke: '#0077B6' }, // 6. Vivid Cerulean (Deep but still "Blue")
		{ fill: '#90E0EF', stroke: '#00B4D8' }, // 7. Mist Blue (Bright and punchy)
	];

	/**
	 * Maps AI Suggestions to the formal Place interface.
	 * Handles background-hydrated image data and coordinate-based IDs.
	 */
	const mapAiSuggestionsToPlaces = (hydratedSpots) => {

		// Shorten the why_stop text (Reasoning)
		const truncateReason = (text, limit) => {
			const words = text.split(' ');
			return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text;
		};

		// Helper to create a safe ID from the name
		const slugify = (text) => text
			.toString()
			.toLowerCase()
			.trim()
			.replace(/\s+/g, '-')     // Replace spaces with -
			.replace(/[^\w-]+/g, '')  // Remove all non-word chars
			.replace(/--+/g, '-');    // Replace multiple - with single -

		const seenIds = new Set();

		return hydratedSpots.map((spot, index) => {

			// Pick a color based on the index (loop back to 0 if more than 6 spots)
			const colorPair = gemBluePalette[index % gemBluePalette.length];

			// 1. Process the punchy reason (Max 10 words + smart ellipsis)
			const firstSentence = spot.why_stop.split('.')[0];
			const punchyReason = truncateReason(firstSentence, 7);

			// 2. Format Strapline: "Reasoning. Town"
			const formattedStrapLine = `${punchyReason}. ${spot.town}`;

			const namePart = slugify(spot.name || 'gem');
			const townPart = slugify(spot.town || 'area');

			let baseId = `ai-${namePart}-${townPart}`;
			const uniqueId = baseId;

			let counter = 1;

			// If we already have this ID in THIS specific search result, append a number
			while (seenIds.has(uniqueId)) {
				uniqueId = `${baseId}-${counter}`;
				counter++;
			}
			seenIds.add(uniqueId);

			return {
				id: uniqueId,
				place_id: uniqueId,
				map_id: `ai-${index + 1}`,
				title: spot.name,
				address: spot.town,
				city: spot.town,
				position: { lat: spot.lat, lng: spot.lng },
				show_photos: true,
				limit_detail: true,
				is_hub: false,
				description: spot.description, // Full text stays in the detail view
				strapLine: formattedStrapLine,  // Shortened text for the list view
				fullReasoning: spot.why_stop,
				categories: [{ id: 'ai-result', name: 'Ai Pick' }],
				placeType: "AI_GEM",
				icon: markerIcons["AI Suggestion"] || markerIcons["Sightseeing"],
				fillColor: colorPair.fill,
				strokeColor: colorPair.stroke,
				images: spot.imageUrl ? [{
					image_url: spot.imageUrl,
					caption: spot.name,
					attribution: spot.attribution,
					original_source_url: spot.descriptionUrl
				}] : []
			};
		});
	};

	const handleGalleryItemClick = (place) => {
		// 1. Safety check: AI_SUMMARY items won't have a position
		if (!place || !place.position) return;

		const allSuggestedMarkers = hubMarkersRef.current;

		// 2. Find the marker. 
		const marker = hubMarkersRef.current.find((m) => m && m.getData() === place.id);

		if (marker) {
			// 3. Focus the map using your existing smooth animation logic
			focusMapOnMarker(marker, place);

			// 4. Update selected state for list highlighting
			setSelectedPlace(place);

			// 5. Scroll the PlacesList to the correct item
			const element = placeRefs.current[place.place_id];
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' });

				setTimeout(() => {
					const rect = element.getBoundingClientRect();

					// 1. Check if the element is actually visible to the user's eye
					const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

					if (!isVisible) {
						// 2. The Fallback: Manual Window Scroll
						// We target 'places-overlay' because that's the main section we want to jump to
						const container = document.querySelector('.places-overlay');

						if (container) {
							// We calculate how far the container is from the very top of the document
							const containerTop = container.getBoundingClientRect().top + window.pageYOffset;

							// We scroll the WINDOW to the top of that container (minus a 10px buffer)
							window.scrollTo({
								top: containerTop - 10,
								behavior: 'smooth'
							});

							// 3. Manual Internal Scroll
							// Now that the page is moving, we force the UL/Container to show the item
							// We use 'block: "nearest"' to avoid re-triggering a full page jump
							setTimeout(() => {
								element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
							}, 300);
						}
					}
				}, 1000);
			}
		} else {
			console.warn("No map marker found for place:", place.id);
		}
	};

	const onExecuteSearch = async () => {
		setError(null);

		if (!location1 || !location2) {
			setError("To scout for gems, please enter both a start and end location. We find the best spots right in between!");
			return;
		}

		try {
			if (searchType === 'ai') {
				await handleAiSubmit(); // Await the result
			} else {
				await handleFind(); // Await the result
			}
		} catch (err) {
			console.error("Search failed:", err);
			setError("Something went wrong while gem scouting. Please try again.");
		}
	};

	useEffect(() => {
		// If we just switched to AI, clear any static 'Web Search' text
		// so the placeholder suggestions can start rotating again.
		if (searchType === 'ai') {
			setAiPrompt('');
		} else {
			// Optional: If switching to Web, you might want to set the 
			// static reminder text as the actual value if it's uneditable.
			setAiPrompt('');
		}
	}, [searchType, setAiPrompt]);

	const searchAreaPlaceholder = useMemo(() => {
		// If we are in Web Search mode, always show the static default
		if (searchType !== 'ai') {
			return NOAI_SEARCH_DEFAULT;
		}

		// If we are in AI mode AND the user hasn't typed anything yet, 
		// show the rotating suggestions.
		if (aiPrompt.length === 0) {
			return SEARCH_SUGGESTIONS[suggestionIndex];
		}

		// If they are typing, we use the standard default (or empty string)
		return NOAI_SEARCH_DEFAULT;
	}, [suggestionIndex, aiPrompt.length, searchType]);

	return (
		<div className={`container ${isMapView ? 'map-view' : 'list-view'}`}>
			{!isMapView && ( // Only show header and search in list-view
				<div className="hero">
					<div className="hero-bg"></div>
					<div className="hero-scrim"></div>
					<div className="hero-content">
						<div className="branding-section">
							<a href="/" onClick={() => window.location.href = '/'}>
								<img src="/betweenlogo.png" alt="What Is Between" className="brand-logo-image" />
							</a>
							<div className="brand-text">
								<h1 className="site-name-text">WHAT IS BETWEEN</h1>
								<h2 className="vision-message">Find places on the way or halfway</h2>
							</div>
						</div>
						<div className="search-panel">
							<div className="location-input-wrapper">
								<input
									type="text"
									ref={inputRef1}
									value={location1}
									onChange={(e) => setLocation1(e.target.value)}
									placeholder="From location"
									className="input-box"
									onKeyDown={(e) => e.key === 'Enter' && onExecuteSearch()}
								/>
								<span className="location-icon" onClick={handleGetCurrentLocation}>⊕</span>
							</div>

							<div className="location-input-wrapper">
								<input
									type="text"
									ref={inputRef2}
									value={location2}
									onChange={(e) => setLocation2(e.target.value)}
									placeholder="To location"
									className="input-box"
									onKeyDown={(e) => e.key === 'Enter' && onExecuteSearch()}
								/>
							</div>

							<div className="ai-row">
								<div className="ai-input-wrapper">
									<textarea
										className="input-box ai-textarea"
										placeholder={searchAreaPlaceholder}
										value={aiPrompt}
										onChange={(e) => setAiPrompt(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && !e.shiftKey) {
												e.preventDefault();
												onExecuteSearch();
											}
										}}
									/>

									{isLoading && (
										<div className="ai-inline-loader">
											<div className="spinner-mini"></div>
											<span>Scouting For Gems</span>
										</div>
									)}
								</div>

								<div className="split-button-wrapper">
									<button
										className="split-toggle-btn"
										onClick={() => setIsFindButtonDropdownOpen(!isFindButtonDropdownOpen)}
									>
										{searchType === 'ai' ? '✨ AI Search' : '🌐 Web Search'} ▾
									</button>

									{isFindButtonDropdownOpen && (
										<div className="split-dropdown-menu">
											<div onClick={() => { setSearchType('ai'); setIsFindButtonDropdownOpen(false); }}>✨ AI Search</div>
											<div onClick={() => { setSearchType('web'); setIsFindButtonDropdownOpen(false); }}>🌐 Web Search</div>
										</div>
									)}
								</div>

								<button className="find-arrow-button" onClick={onExecuteSearch}>›</button>
							</div>
							<div className="ai-legal-footer">
								Powered by Google & Gemini AI.
								<a
									href="/site/privacy.html"
									className="ai-legal-link"
									onClick={(e) => {
										e.preventDefault();
										window.dispatchEvent(new Event('open-privacy-modal'));
									}}
								>
									Privacy
								</a>
							</div>
						</div>
					</div>
				</div>
			)}
			{error && <p className="error-message">{error}</p>}
			{(midpoint || focalPoint) && (
				<>
					<div className="map-wrapper-listview-height" style={{ display: 'none' }}></div> {/* Hidden element */}
					{!isMapView && !hideHubs && featuredHubPhotos.length > 0 && (
						<div className="hub-photo-strip">
							{featuredHubPhotos.map((photo, index) => (
								<div key={index} className="featured-hub-image-element">
									<a href={photo.contextLink} target="_blank" rel="noopener noreferrer">
										<img src={photo.link} alt={photo.title} />
										{photo.title && (
											photo.is_hub_hero ? (
												<div className="heading-overlay" style={{ opacity: 1 }}>
													<div className="heading-title">{photo.title}</div>
												</div>
											) : (
												<div className="image-overlay" style={{ opacity: 1 }}>
													<div className="image-title">{photo.title}</div>
												</div>
											)
										)}
									</a>
								</div>
							))}
						</div>
					)}
					<div className="grid-container">
						<div className="map-wrapper">
							{!isDropdownOpen && (
								<div
									className="map-overlay"
									onClick={() => {
										//console.log("Map overlay clicked");
										if (!isDropdownOpen) {
											toggleMapView();
										}
									}}
								></div>
							)}
							<div ref={mapRefCallback} className="map" id="map"></div>
							{isMapView && isMapPlacesListVisible && (
								<div className="places-overlay map-view-mobile-style">
									<PlacesList
										key={isMapView ? 'map-view' : 'list-view'} // Add a key to force re-render
										places={placesWithMapIds}
										eventPlaces={eventVenues}
										likedPlaces={likedPlaces}
										setLikedPlaces={setLikedPlaces}
										markers={markersRef.current}
										eventMarkers={eventMarkersRef.current}
										hubGemMarkers={hubMarkersRef.current}
										selectedPlaceRef={selectedPlaceRef}
										selectedPlace={selectedPlace}
										setSelectedPlace={setSelectedPlace}
										placeRefs={placeRefs}
										location1LL={location1LL}
										calculateDistance={calculateDistance}
										expandedReviews={expandedReviews}
										toggleReview={toggleReview}
										focusMapOnMarker={focusMapOnMarker}
										displayedTitle=""
										ensurePlaceMarkersVisible={ensurePlaceMarkersVisible}
										handleShare={handleShare}
									/>
								</div>
							)}
						</div>
						{!isMapView && ( // Only show lists in list-view
							<>
								<PlacesList
									key={isMapView ? 'map-view' : 'list-view'}
									places={placesWithMapIds}
									eventPlaces={eventVenues}
									likedPlaces={likedPlaces}
									setLikedPlaces={setLikedPlaces}
									markers={markersRef.current}
									eventMarkers={eventMarkersRef.current}
									hubGemMarkers={hubMarkersRef.current}
									selectedPlaceRef={selectedPlaceRef}
									selectedPlace={selectedPlace}
									setSelectedPlace={setSelectedPlace}
									placeRefs={placeRefs}
									location1LL={location1LL}
									calculateDistance={calculateDistance}
									expandedReviews={expandedReviews}
									toggleReview={toggleReview}
									focusMapOnMarker={focusMapOnMarker}
									displayedTitle={dynamicTitle}
									ensurePlaceMarkersVisible={ensurePlaceMarkersVisible}
									handleShare={handleShare}
								/>
								{!hideEvents && (
									<EventsList
										events={events}
										markers={eventMarkersRef.current}
										selectedEventRef={selectedEventRef}
										selectedEvent={selectedEvent}
										setSelectedEvent={setSelectedEvent}
										eventRefs={eventRefs}
										location1LL={location1LL}
										calculateDistance={calculateDistance}
										expandedDescriptions={expandedDescriptions}
										toggleDescription={toggleDescription}
										focusMapOnMarker={focusMapOnMarker}
										displayedTitle="Culture"
									/>
								)}
							</>
						)}
					</div>
					{!isMapView && ( // Only show masonry gallery in list-view
						<div className="image-grid-container">
							<PlacesMasonryGallery
								items={galleryItems}
								setItems={setGalleryItems}
								onItemClick={handleGalleryItemClick}
								selectedPlace={selectedPlace}
								isMapView={isMapView}
							/>
						</div>
					)}
				</>
			)}
			{initialContentDisplayed && (
				<>
					<div className="image-grid-container">
						<div>
							<div className="static-gallery-class">

								<div className="static-image-element">
									<a href="/" target="_blank" rel="noopener noreferrer">
										<img src="/entertwolocationstofindplaceswithicons.png" alt="Enter locations to find great places halfway or on your journey" />
										<div className="image-overlay" style={{ opacity: 0 }}>
											<div className="image-title">Enter locations to find great places halfway or on your journey</div>
										</div>
									</a>
								</div>
								<div>
									<div className="static-image-element">
										<a href="/site/uk/london-manchester/r1.html" rel="noopener noreferrer">
											<img src="/images/leicester/leicesterpostcard4x.png" alt="Leicester Halfway between London and Manchester" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Great places halfway between London and Manchester</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#464B54', color: '#FFFFFF' }}>
										<div className="image-title">Trip From London to Manchester</div>
										<div className="image-attribution">
											Convenient On Route Places to Visit
										</div>
									</div>
								</div>
								<div>
									<div className="static-image-element">
										<a href="/site/uk/manchester-cornwall/r1.html" rel="noopener noreferrer">
											<img src="/images/bristol/BristolPostcard4x4.png" alt="Halfway between Manchester & Cornwall" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Good places halfway and between the North West & Cornwall</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#464B54', color: '#FFFFFF' }}>
										<div className="image-title">Trips From the North West to Cornwall</div>
										<div className="image-attribution">
											Convenient On Route Places to Visit
										</div>
									</div>
								</div>
								<div>
									<div className="static-image-element">
										<a href="https://honiton.nub.news/news/local-news/review-otter-valley-ice-cream-and-field-kitchen-190640" target="_blank" rel="noopener noreferrer">
											<img src="/images/london-cornwall/ottervalleyfort.jpeg" alt="Otter Valley" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Otter Valley Kitchen</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#BAD6E2', color: '#000' }}>
										<div className="image-title">Otter Valley Kitchen</div>
										<div className="image-attribution">Otter Valley Ice Cream And Field Kitchen</div>
									</div>
								</div>

								<div>
									<div className="static-image-element">
										<a href="https://www.facebook.com/toasthoniton/?locale=en_GB" target="_blank" rel="noopener noreferrer">
											<img src="/images/london-cornwall/toast-courtyard2.jpeg" alt="TOAST CAFE" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Toast Cafe</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#BAD6E2', color: '#000' }}>
										<div className="image-title">Toast Cafe, Honiton</div>
										<div className="image-attribution">
											<a
												href="https://www.facebook.com/toasthoniton/?locale=en_GB"
												target="_blank"
												rel="noopener noreferrer"
												style={{ color: 'inherit', textDecoration: 'none' }}
											>
												Toast Honiton
											</a>
										</div>
									</div>
								</div>

								<div>
									<div className="static-image-element">
										<a href="https://yellowdeli.uk/" target="_blank" rel="noopener noreferrer">
											<img src="/images/london-cornwall/yellowdeli2-honiton1.jpg" alt="THE YELLOW DELI, Honiton - Restaurant" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">The Yellow Deli, Honiton</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#BAD6E2', color: '#000' }}>
										<div className="image-title">The Yellow Deli, Honiton</div>
										<div className="image-attribution">https://yellowdeli.uk/</div>
									</div>
								</div>

								<div>
									<div className="static-image-element">
										<a href="https://www.ottervalleydairy.co.uk/" target="_blank" rel="noopener noreferrer">
											<img src="/images/london-cornwall/ottervalleykitchen.jpeg" alt="Otter Valley Kitchen" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Otter Valley Kitchen</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#BAD6E2', color: '#000' }}>
										<div className="image-title">Otter Valley Kitchen</div>
										<div className="image-attribution">
											<a
												href="https://www.ottervalleydairy.co.uk/"
												target="_blank"
												rel="noopener noreferrer"
												style={{ color: 'inherit', textDecoration: 'none' }}
											>
												ottervalleydairy.co.uk
											</a>
										</div>
									</div>
								</div>
								<div>
									<div className="static-image-element" style={{ paddingBottom: '57.29%' }}>
										<a href="/?location1=London%2C+England%2C+United+Kingdom&location2=Cornwall%2C+England%2C+United+Kingdom&routeIndex=0&filters=Eat+%26+Drink&likedPlaces=&selectedPlaceId=" rel="noopener noreferrer">
											<img src="/images/london-cornwall/londontocornwallhalfwayhubs2.png" alt="London Cornwall Halfway" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Trip From London - Suggested Convenient On Route Places</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#EBF5FF', color: '#000' }}>
										<div className="image-title">Trip From London</div>
										<div className="image-attribution">
											Convenient On Route Places
										</div>
									</div>
								</div>

								<div className="static-home-places-element">
									<div className="places-overlay">
										<div className="places-overlay-content">
											<h4 className="list-title">
												Trip from London to Cornwall 407km
												<br />
												Convenient Places To Consider
											</h4>
											<ul className="places-list">
												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(186, 214, 226)",
																	borderRadius: "50%",
																	border: "1px solid rgb(4, 100, 129)",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	1
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Honiton</h5>
														</div>
														<div>
															<span className="place-category">Convenient</span>
															<div>
																<div>
																	<p>Boutique cafes and food.</p>
																</div>
																<span className="km-distance">
																	<p>@228 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Honiton%2C%20Honiton"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(150, 225, 245)",
																	borderRadius: "50%",
																	border: "1px solid rgb(1, 162, 222)",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	2
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Salisbury</h5>
														</div>
														<div>
															<span className="place-category">Charming</span>
															<div>
																<div>
																	<p>Deep history in beautiful area.</p>
																</div>
																<span className="km-distance">
																	<p>@126 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Salisbury%2C%20Salisbury"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(111, 230, 240)",
																	borderRadius: "50%",
																	border: "1px solid rgb(1, 162, 222)",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	3
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Stone Henge</h5>
														</div>
														<div>
															<span className="place-category">Major Icon</span>
															<div>
																<div>
																	<p>Breathtaking heritage in stunning area</p>
																</div>
																<span className="km-distance">
																	<p>@124 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Stone%20Henge%2C%20Stone%20Henge"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>
												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(4, 166, 248)",
																	borderRadius: "50%",
																	border: "1px solid rgb(4, 100, 129)",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	4
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Winchester</h5>
														</div>
														<div>
															<span className="place-category">Charming</span>
															<div>
																<div>
																	<p>Pretty, walkable culture.</p>
																</div>
																<span className="km-distance">
																	<p>@96 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Winchester%2C%20Winchester"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(0, 230, 77)",
																	borderRadius: "50%",
																	border: "1px solid black",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	5
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Otter Valley Kitchen</h5>
														</div>
														<div>
															<span className="place-category">Our Pick</span>
															<div>
																<p>Otter Valley Kitchen</p>
																<span className="km-distance">
																	<p>@226 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Otter%20Valley%20Kitchen%2C%20Otter%20Valley%20Kitchen"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(0, 230, 77)",
																	borderRadius: "50%",
																	border: "1px solid black",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	6
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Boston Tea Party</h5>
														</div>
														<div>
															<span className="place-category">Our Pick</span>
															<div>
																<p>Boston Tea Party</p>
																<span className="km-distance">
																	<p>@228 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Boston%20Tea%20Party%2C%20Boston%20Tea%20Party"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(0, 230, 77)",
																	borderRadius: "50%",
																	border: "1px solid black",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	7
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>Toast Café</h5>
														</div>
														<div>
															<span className="place-category">Our Pick</span>
															<div>
																<p>Toast Café</p>
																<span className="km-distance">
																	<p>@228 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=Toast%20Caf%C3%A9%2C%20Toast%20Caf%C3%A9"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(0, 230, 77)",
																	borderRadius: "50%",
																	border: "1px solid black",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	8
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>The Yellow Deli</h5>
														</div>
														<div>
															<span className="place-category">Our Pick</span>
															<div>
																<p>The Yellow Deli</p>
																<span className="km-distance">
																	<p>@228 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=The%20Yellow%20Deli%2C%20The%20Yellow%20Deli"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>

												<li className="place-item">
													<div className="title-distance-container">
														<div style={{ display: "flex", alignItems: "center" }}>
															<div
																style={{
																	width: 17,
																	height: 17,
																	backgroundColor: "rgb(0, 230, 77)",
																	borderRadius: "50%",
																	border: "1px solid black",
																	display: "flex",
																	justifyContent: "center",
																	alignItems: "center",
																	marginRight: 10,
																}}
															>
																<span
																	style={{
																		fontSize: 11,
																		fontFamily: "Arial",
																		color: "black",
																		fontWeight: "bold",
																	}}
																>
																	9
																</span>
															</div>
															<img src="/sh-heart-off.png" alt="Like" className="like-icon" />
															<h5>The Ilchester Arms</h5>
														</div>
														<div>
															<span className="place-category">Hotel</span>
															<div>
																<p>Church Street, Yeovil</p>
																<span className="km-distance">
																	<p>@186 km</p>
																</span>
															</div>
														</div>
													</div>
													<div className="place-moreinfo-container">
														<a
															href="https://www.google.com/maps/search/?api=1&query=The%20Ilchester%20Arms%2C%20The%20Ilchester%20Arms%2C%20Church%20Street%2C%20Yeovil%2C%20BA22%208LN%2C%20United%20Kingdom"
															target="_blank"
															rel="noopener noreferrer"
														>
															Detail
														</a>
													</div>
												</li>
												<li className="last-place-item"></li>
											</ul>
										</div>
									</div>
								</div>


								<div>
									<div className="static-image-element">
										<a href="https://www.visitdevon.co.uk/east-devon/areas-to-visit/honiton/" target="_blank" rel="noopener noreferrer">
											<img src="/images/london-cornwall/Musbury,_the_Axe_valley_-_geograph.org.uk_-_492943.jpg" alt="Honiton Landscape" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Honiton Landscape</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#BAD6E2', color: '#000' }}>
										<div className="image-title">The Axe valley - Rising towards Honiton</div>
										<div className="image-attribution">Musbury: the Axe valley - Martin Bodman - CC BY-SA 2.0</div>
									</div>
								</div>

								<div>
									<div className="static-image-element">
										<a href="/?location1=London%2C+England%2C+United+Kingdom&location2=Cornwall%2C+England%2C+United+Kingdom&routeIndex=0&filters=Eat+%26+Drink&likedPlaces=&selectedPlaceId=" target="_blank" rel="noopener noreferrer">
											<img src="/images/london-cornwall/Honiton_Town_Centre_-_geograph.org.uk_-_5180741.jpg" alt="Honiton Highstreet" />
											<div className="image-overlay" style={{ opacity: 0 }}>
												<div className="image-title">Honiton Highstreet</div>
											</div>
										</a>
									</div>
									<div className="image-color-bar" style={{ backgroundColor: '#BAD6E2', color: '#000' }}>
										<div className="image-title">Honiton Highstreet</div>
										<div className="image-attribution">
											<a
												href="https://commons.wikimedia.org/wiki/File:Honiton_Town_Centre_-_geograph.org.uk_-_5180741.jpg"
												target="_blank"
												rel="noopener noreferrer"
												style={{ color: 'inherit', textDecoration: 'none' }}
											>
												Des Blenkinsopp - CC BY-SA 2.0
											</a>
										</div>
									</div>
								</div>

								<div className="static-image-element">
									<div className="static-item-text">
										<h1>The best places might be just off the road</h1>
										<h2>Discover what’s on your way</h2>
										<h3>On the road, meeting friends, or taking a break. We help you uncover great places.</h3>
										<h3>
											We'd love to support you in global adventures, and today we’ve picked some great places along the UK’s most iconic journeys.
											Covering scenic, culture-rich favourites of UK and international travellers.
										</h3>
									</div>
								</div>

								<div className="static-image-element">
									<a href="https://bostonteaparty.co.uk/cafes/honiton/" target="_blank" rel="noopener noreferrer">
										<img src="/images/london-cornwall/btp.jpg" alt="Boston Tea Party Honiton" />
										<div className="image-overlay">
											<div className="image-title">Boston Tea Party Honiton</div>
										</div>
									</a>
								</div>

							</div>
						</div>
					</div>
				</>


			)}

			{!isMapView && (
				<Footer />
			)}

			{isMapView && ( // Show the "List View" button in map-view
				<div className="map-view-button-container">
					<button onClick={handleListViewClick} className="map-view-button">
						Back
					</button>
				</div>
			)}

			{placeDetailSheet && (
				<PlaceDetailSheet
					place={placeDetailSheet}
					relatedPlaces={sheetRelatedPlaces}
					isSearching={isLoading}
					sheetRef={placeDetailSheetRef}
					onSelectPlace={(picked) => {
						sheetOpenedViaDeepLinkRef.current = false;
						setPlaceDetailSheet(picked);
						setSheetRelatedPlaces([]);
						if (mapInstance.current && picked.position) {
							mapInstance.current.setCenter({ lat: picked.position.lat, lng: picked.position.lng });
							setTimeout(() => { applySheetMarkerOffset(picked.position); }, 100);
						}
					}}
					onClose={() => {
						sheetOpenedViaDeepLinkRef.current = false;
						setPlaceDetailSheet(null);
						setSheetRelatedPlaces([]);
						// HERE Maps doesn't know the overlay shrank — force a repaint
						setTimeout(() => { mapInstance.current?.getViewPort().resize(); }, 50);
					}}
				/>
			)}
		</div>
	);
};

export default MapBetweenPlaces;